import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip as ChartTooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { b2bFetch, clearB2BToken, getB2BToken, getB2BImpersonator, clearB2BImpersonator } from './auth';
import AnnouncementBody from '../components/AnnouncementBody';
import c2pCentralLoginLink from '../components/c2pCentralLink';
import DocumentBrowser from '../components/DocumentBrowser';
import DocumentViewerModal from '../components/DocumentViewerModal';
import TransitionWorkspace from './Transition';

ChartJS.register(BarElement, CategoryScale, LinearScale, ChartTooltip);

/* B2B ("entrepreneur") workspace. Self-contained — talks to the API only
 * through b2bFetch (Bearer token), no intranet imports.
 *
 * Left-menu shell: firm admins get the firm-wide sections (Usage, Members,
 * Invoices, and Payment Method at the bottom); standard members get their own
 * usage. Onboarding lives only in the one-time guided sign-up (SetupWizard) —
 * once a firm is set up it drops out of the menu. The planned firm-scoped Work
 * view (over PCA clients/tools) will slot in here as another menu item — this
 * Claude-reseller functionality sits alongside it, not in place of it.
 */

const c = {
  navy:   '#0a2947',
  gold:   '#d09b2c',
  teal:   '#0069a6',
  green:  '#1f8a54',
  amber:  '#b9770e',
  red:    '#9a1c1c',
  ink:    '#1d2733',
  subtle: '#58585b',
  line:   '#e9e6e1',
  canvas: '#e9e6e1',   // hub's warm cream page canvas
  surface:'#ffffff',
};

const ROLE_LABEL = { B2B_ADMIN: 'Firm Admin', B2B_STANDARD: 'Standard' };

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

// Stripe returns card brands like "visa"/"amex"/"mastercard". Present the common
// ones with their usual casing, and title-case anything else.
const CARD_BRAND_LABEL = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover', diners: 'Diners', jcb: 'JCB', unionpay: 'UnionPay' };
const formatCardBrand = (brand) => {
  if (!brand) return 'Card';
  const key = String(brand).toLowerCase();
  return CARD_BRAND_LABEL[key] || (key.charAt(0).toUpperCase() + key.slice(1));
};

// Fetches a one-time document download/view URL exactly once and hands the
// browser an in-memory blob: URL instead of ever navigating a tab to the
// (single-use) server URL directly — a plain navigation leaves the link
// sitting where something other than the user's own click (a browser's
// built-in PDF viewer probing it, a corporate AV/proxy scanning it) can
// independently fetch it first and burn its one use. A blob: URL is
// in-memory only and isn't independently fetchable by anything.
async function fetchAsBlobUrl(url) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Could not retrieve the file (HTTP ${resp.status}).`);
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

// Where the firm is in getting its Claude workspace live, derived from the firm's
// application + domain state. Surfaced in the dashboard header so admins and
// members alike can see progress (pending DNS → pending activation → Active).
function claudeStatus(applicationStatus, domainStatus) {
  switch (applicationStatus) {
    case 'ACTIVE':
      return { label: 'Active', fg: '#1f8a54', bg: 'rgba(31,138,84,0.10)' };
    case 'APPROVED':
      return domainStatus === 'VERIFIED'
        ? { label: 'Pending activation', fg: '#b9770e', bg: 'rgba(185,119,14,0.12)' }
        : { label: 'Pending DNS', fg: '#b9770e', bg: 'rgba(185,119,14,0.12)' };
    case 'SUBMITTED':
      return { label: 'Pending review', fg: '#0069a6', bg: 'rgba(0,105,166,0.10)' };
    case 'SUSPENDED':
      return { label: 'Suspended', fg: '#9a1c1c', bg: 'rgba(154,28,28,0.10)' };
    case 'REJECTED':
      return { label: 'Not approved', fg: '#9a1c1c', bg: 'rgba(154,28,28,0.10)' };
    default:
      return { label: 'Not started', fg: '#58585b', bg: 'rgba(88,88,91,0.08)' };
  }
}

// Firm name + Claude status banner shown atop the workspace.
function FirmHeader({ firmName, applicationStatus, domainStatus, hasSeat }) {
  const s = claudeStatus(applicationStatus, domainStatus);
  // Claude is optional, per firm AND per person. The pill is only for someone
  // who has a Claude seat requested or live with us (admin or standard) - a
  // hub-only member has no Claude status to track, even when their firm has
  // signed up. The firm must have applied too, or it would just say
  // "Not started".
  const showClaude = hasSeat && !!applicationStatus && applicationStatus !== 'DRAFT';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
      {/* The "Prosperity Entrepreneur" eyebrow that used to sit above this is
          gone — the sidebar brand says it on every page, so repeating it here
          just pushed the thing the reader actually wants (whose firm am I
          looking at) down a line. */}
      <h1 style={{ margin: 0, color: c.gold, fontWeight: 800, fontSize: '1.5rem', wordBreak: 'break-word' }}>
        {firmName || 'Your firm'}
      </h1>
      {showClaude && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: s.bg, color: s.fg, borderRadius: 20, padding: '0.35rem 0.85rem', fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
          <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: s.fg }} />
          Claude: {s.label}
        </div>
      )}
    </div>
  );
}
FirmHeader.propTypes = {
  firmName: PropTypes.string,
  hasSeat: PropTypes.bool,
  applicationStatus: PropTypes.string,
  domainStatus: PropTypes.string,
};

// Persistent workspace banner shown until the signed-in user finishes their
// Claude certification ("Claude 101 training"). Finishing the 3-step sign-up
// wizard does NOT complete a firm — every person still has to read the AI
// policies, complete the training, and upload their certificate. Until that's
// submitted (submitted_at, set automatically once all cert steps are done) we
// make the outstanding action obvious with a call to action; once submitted we
// simply tell them they're pending and PCA will reach out. Once the firm is
// fully ACTIVE there's nothing pending, so the banner disappears.
// The Claude pages - the only ones that carry the certification banner. The
// rest of the workspace (dashboard, training, resources, members) isn't about
// Claude, so a "finish your sign-up" warning there reads as a fault in the
// whole account.
const CLAUDE_TABS = ['claude', 'usage', 'invoices', 'wallet'];

function WorkspaceBanner({ currentTab, applicationStatus, hasSeat, onGoToCertification }) {
  const [status, setStatus] = useState(null);
  const [ready, setReady] = useState(false);

  // Re-fetch whenever the tab changes so the banner reflects a certification
  // the user just finished on the Claude Certification tab.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await b2bFetch('/api/b2b/claude_access_status');
        if (!cancelled) setStatus(d);
      } catch {
        if (!cancelled) setStatus(null); // fail quiet — never block the workspace
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [currentTab]);

  if (!ready || !status) return null;
  // Certification is only asked of people who have a Claude seat requested or
  // live. A hub-only member has nothing pending with us.
  if (!hasSeat) return null;

  const submitted = !!status.submitted_at;
  // Has the firm actually begun its Claude sign-up? DRAFT — or no firm account
  // row at all, which /b2b/me also reports as DRAFT — means it hasn't. Staff can
  // create a firm and add its members before the firm admin ever applies, so
  // this is a real state with real people sitting in it.
  const firmStarted = !!applicationStatus && applicationStatus !== 'DRAFT';

  if (submitted) {
    // Done on their end. Nothing left to nudge once the firm is live.
    if (applicationStatus === 'ACTIVE') return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', background: 'rgba(0,105,166,0.08)', border: '1px solid rgba(0,105,166,0.25)', borderLeft: `4px solid ${c.teal}`, borderRadius: 10, padding: '0.9rem 1.15rem', marginBottom: '1.25rem' }}>
        <i className="bi bi-hourglass-split" style={{ color: c.teal, fontSize: '1.4rem', flexShrink: 0 }} />
        <div>
          <div style={{ fontWeight: 800, color: c.navy, fontSize: '0.95rem' }}>Your account is pending</div>
          <div style={{ color: c.subtle, fontSize: '0.85rem', lineHeight: 1.5 }}>
            You’ve completed everything on your end. Your account is pending — the Prosperity team will reach out to you shortly.
          </div>
        </div>
      </div>
    );
  }

  // Nothing to chase before the firm has applied: the copy below promises
  // "Your application is in", which would simply be untrue, and warning someone
  // about an unfinished step they were never asked to take reads as a fault in
  // their account. The "Sign up for Claude" menu item is the right prompt at
  // this stage, and it's already there.
  if (!firmStarted) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', background: 'rgba(185,119,14,0.10)', border: '1px solid rgba(185,119,14,0.35)', borderLeft: `4px solid ${c.amber}`, borderRadius: 10, padding: '1rem 1.15rem', marginBottom: '1.25rem' }}>
      <i className="bi bi-exclamation-triangle-fill" style={{ color: c.amber, fontSize: '1.5rem', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontWeight: 800, color: c.navy, fontSize: '0.95rem' }}>One step left to finish your sign-up</div>
        <div style={{ color: c.subtle, fontSize: '0.85rem', lineHeight: 1.5 }}>
          Your application is in, but your account stays <strong>pending</strong> until you complete your Claude certification training. Finish the steps to move forward.
        </div>
      </div>
      {currentTab !== 'claude' && (
        <button type="button" onClick={onGoToCertification}
          style={{ background: c.gold, color: 'white', border: 'none', borderRadius: 8, padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
          <i className="bi bi-arrow-right-circle" />Complete Claude Certification
        </button>
      )}
    </div>
  );
}
WorkspaceBanner.propTypes = {
  currentTab: PropTypes.string,
  applicationStatus: PropTypes.string,
  hasSeat: PropTypes.bool,
  onGoToCertification: PropTypes.func,
};

const STATUS_STYLE = {
  over:        { label: 'Over limit',  bg: 'rgba(154,28,28,0.10)',  fg: c.red },
  approaching: { label: 'Approaching', bg: 'rgba(185,119,14,0.12)', fg: c.amber },
  ok:          { label: 'OK',          bg: 'rgba(31,138,84,0.10)',  fg: c.green },
  no_usage:    { label: 'No usage',    bg: 'rgba(88,88,91,0.08)',   fg: c.subtle },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.no_usage;
  return (
    <span style={{ background: s.bg, color: s.fg, borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>
      {s.label}
    </span>
  );
}

StatusBadge.propTypes = { status: PropTypes.string };

const card = { background: c.surface, borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.1)', padding: '1.5rem' };

// Hub-style content card: white, 12px radius, soft navy shadow, with a navy
// header bar carrying a 3px goldenrod bottom border and an uppercase, letter-
// spaced title (matches the internal employee hub — see routes/Dashboard.js).
// `actions` renders right-aligned controls in the header (e.g. a month picker).
function Panel({ title, icon, actions, children, bodyStyle }) {
  return (
    <div style={{ background: c.surface, borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden' }}>
      <div style={{ background: c.navy, borderBottom: `3px solid ${c.gold}`, padding: '0.8rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap' }}>
        {icon && <i className={`bi bi-${icon}`} style={{ color: c.gold, fontSize: '0.95rem' }} />}
        <h3 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>{title}</h3>
        {actions && <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>{actions}</div>}
      </div>
      <div style={{ padding: '1.25rem', ...bodyStyle }}>{children}</div>
    </div>
  );
}
Panel.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  actions: PropTypes.node,
  children: PropTypes.node,
  bodyStyle: PropTypes.object,
};

// Height of the mobile top bar. Enough for a 44px tap target on the hamburger
// (the Apple/WCAG floor) plus a hairline of breathing room.
const MOBILE_BAR = 52;

// Dependency-free viewport check — this module stays self-contained (no intranet
// imports), so we don't pull in ../utils' useIsMobile. Mobile ≤ 820px.
function useIsMobile(breakpoint = 820) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// ── Sidebar building blocks ───────────────────────────────────────────────────
// A group is a heading that unfolds; its children are indented and share the
// same active/hover treatment as top-level items, so the menu reads as one thing
// rather than two.
//
// THREE levels of nesting, because the Resource Library needs them: it is one
// group holding the Marketing sub-group (whose children are the marketing
// sections) alongside the reference pages published to the firms, and those
// pages can themselves sit in a named folder — the same shape as
// Desk → Resource Library in the employee hub, which is the point:
//
//   Resource Library
//     Marketing            <- the collateral library, one child per section
//       Monthly Toolkit
//     Salesforce           <- a resource-library folder, holding pages
//       How-Tos
//     Getting Started      <- a page in no folder
//
// `depth` is carried explicitly rather than inferred, and the indent/size
// tables below are clamped, so a fourth level would render legibly rather than
// falling off the left edge. The last step is deliberately smaller than the
// ones before it: the indent has to keep earning its place against a 260px
// sidebar, and a page title is longer than a section name.

const NAV_INDENT = ['1rem', '2rem', '2.85rem', '3.5rem'];
const NAV_FONT = ['0.875rem', '0.84rem', '0.8rem', '0.78rem'];
const navStep = (table, depth) => table[Math.min(depth, table.length - 1)];

function NavItem({ item, active, hovered, setHovered, onClick, depth, certDone }) {
  const hot = hovered === item.id && !active;
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHovered(item.id)} onMouseLeave={() => setHovered(null)}
      style={{
        display: 'flex', alignItems: 'center', width: '100%', textAlign: 'left', cursor: 'pointer',
        background: active ? 'rgba(0,105,166,0.35)' : hot ? 'rgba(255,255,255,0.08)' : 'transparent',
        border: 'none', borderLeft: `3px solid ${active ? c.gold : 'transparent'}`,
        padding: '0.7rem 1rem',
        paddingLeft: `calc(${navStep(NAV_INDENT, depth)} - ${active ? '3px' : '0px'})`,
        fontSize: navStep(NAV_FONT, depth), letterSpacing: '0.2px',
        fontWeight: active ? 600 : 500,
        color: active ? 'white' : hot ? 'white' : 'rgba(255,255,255,0.75)',
      }}>
      <span style={{ flex: 1 }}>{item.label}</span>
      {/* Only the "done" tick. The warning triangle this used to carry could
          only ever land on "Sign up for Claude" — the menu shows that item
          precisely when certDone is false — and signing up is an invitation
          now, not an outstanding obligation. Flagging it as a problem told
          people something was wrong with their account when nothing was. */}
      {item.id === 'claude' && certDone === true && (
        <i
          className="bi bi-check-circle-fill"
          title="Certification complete"
          aria-label="Certification complete"
          style={{ color: c.green, fontSize: '0.95rem', marginLeft: 8 }}
        />
      )}
    </button>
  );
}
NavItem.propTypes = {
  item: PropTypes.object.isRequired,
  active: PropTypes.bool,
  hovered: PropTypes.string,
  setHovered: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  depth: PropTypes.number,
  certDone: PropTypes.bool,
};
NavItem.defaultProps = { depth: 0 };

function NavGroup({ label, open, depth, children }) {
  // `open` from the parent is the initial state, not a lock — a member can fold
  // the section away, and re-opens automatically when they navigate back into it.
  const [expanded, setExpanded] = useState(open);
  useEffect(() => { if (open) setExpanded(true); }, [open]);
  return (
    <div>
      <button onClick={() => setExpanded(v => !v)} aria-expanded={expanded}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem', width: '100%', textAlign: 'left',
          cursor: 'pointer', background: 'transparent', border: 'none', borderLeft: '3px solid transparent',
          padding: '0.7rem 1rem', paddingLeft: navStep(NAV_INDENT, depth),
          fontSize: navStep(NAV_FONT, depth), letterSpacing: '0.2px', fontWeight: 600,
          // A nested heading sits slightly back from a top-level one, so the
          // eye can tell "a section of a section" from "a section".
          color: depth > 0 ? 'rgba(255,255,255,0.78)' : 'rgba(255,255,255,0.9)',
        }}>
        <span style={{ flex: 1 }}>{label}</span>
        <i className={`bi bi-chevron-${expanded ? 'down' : 'right'}`} style={{ fontSize: '0.72rem', opacity: 0.8 }} />
      </button>
      {expanded && children}
    </div>
  );
}
NavGroup.propTypes = {
  label: PropTypes.string.isRequired,
  open: PropTypes.bool,
  depth: PropTypes.number,
  children: PropTypes.node,
};
NavGroup.defaultProps = { depth: 0 };

// Does this entry, or anything below it, hold the open tab? A group sits open
// when one of its pages is showing, and with two levels that question is no
// longer "is the tab one of my children" — the Marketing sub-group's pages are
// grandchildren of the Resource Library group, and a shallow check would leave
// the outer group folded over the very page a member was reading.
// Exported for ResourceLibrary.smoke.test.js — the two-level menu is the only
// place a shallow check would silently fold the wrong group, and that is not
// visible in a diff.
export const navHoldsTab = (item, tab) => (
  item.children
    ? item.children.some(child => navHoldsTab(child, tab))
    : item.id === tab
);

// ── Dashboard (landing page) ──────────────────────────────────────────────────
// Welcome by firm name, then whatever PCA staff have posted under Entrepreneur →
// Announcements. Dated items are upcoming events ("Save the Date"); undated ones
// are standing information. The server has already dropped anything expired or
// past, so this renders what it is given.

const fmtEventDate = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
};

function AnnouncementCard({ item, showDate }) {
  return (
    <div style={{
      border: `1px solid ${item.pinned ? 'rgba(208,155,44,0.5)' : c.line}`,
      borderLeft: `4px solid ${item.pinned ? c.gold : c.teal}`,
      borderRadius: 10, padding: '0.9rem 1.1rem', background: 'white',
    }}>
      {showDate && item.event_date && (
        <div style={{ color: c.teal, fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.3rem' }}>
          <i className="bi bi-calendar-event me-1" />{fmtEventDate(item.event_date)}
        </div>
      )}
      <div style={{ fontWeight: 800, color: c.navy, fontSize: '0.95rem' }}>
        {item.pinned && <i className="bi bi-pin-angle-fill me-1" style={{ color: c.gold }} />}
        {item.title}
      </div>
      {/* Markdown, same renderer the hub side uses - so a link PCA staff put in
          an announcement is clickable here too. */}
      {item.body && (
        <AnnouncementBody
          style={{ color: c.subtle, fontSize: '0.87rem', lineHeight: 1.55, marginTop: '0.25rem' }}
          linkColor={c.teal}
        >
          {item.body}
        </AnnouncementBody>
      )}
    </div>
  );
}
AnnouncementCard.propTypes = { item: PropTypes.object.isRequired, showDate: PropTypes.bool };

function Home({ firstName, onGoToSetup }) {
  const [items, setItems] = useState(null);   // null = still loading

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await b2bFetch('/api/b2b/announcements');
        if (!cancelled) setItems(Array.isArray(d) ? d : []);
      } catch {
        // Never let a news panel break the landing page.
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dated = (items || []).filter(a => a.event_date);
  const undated = (items || []).filter(a => !a.event_date);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {onGoToSetup && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', background: 'rgba(208,155,44,0.12)', border: '1px solid rgba(208,155,44,0.45)', borderLeft: `4px solid ${c.gold}`, borderRadius: 10, padding: '1rem 1.15rem' }}>
          <i className="bi bi-rocket-takeoff-fill" style={{ color: c.gold, fontSize: '1.5rem', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 800, color: c.navy, fontSize: '0.95rem' }}>Finish setting up Claude for your firm</div>
            <div style={{ color: c.subtle, fontSize: '0.85rem', lineHeight: 1.5 }}>
              Three quick steps - your firm&apos;s details, Claude seats and a payment method. Your Claude
              usage and invoices pages unlock once you&apos;re done.
            </div>
          </div>
          <button type="button" onClick={onGoToSetup}
            style={{ background: c.gold, color: 'white', border: 'none', borderRadius: 8, padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
            <i className="bi bi-arrow-right-circle" />Continue set up
          </button>
        </div>
      )}

      <Panel title="Welcome" icon="stars">
        <div style={{ fontWeight: 800, color: c.navy, fontSize: '1.15rem' }}>
          {/* The firm's name is already the page heading above, so greet the
              person. Nullable — staff can add a member with only an email — so
              fall back to a plain greeting rather than rendering "Welcome, ". */}
          {firstName ? `Welcome, ${firstName}` : 'Welcome'}
        </div>
        <div style={{ color: c.subtle, fontSize: '0.88rem', lineHeight: 1.55, marginTop: '0.25rem' }}>
          This is your firm&apos;s Prosperity Entrepreneur portal. Anything the Prosperity team needs
          you to know shows up here.
        </div>
      </Panel>

      {dated.length > 0 && (
        <Panel title="Save the Date" icon="calendar-event">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {dated.map(a => <AnnouncementCard key={a.id} item={a} showDate />)}
          </div>
        </Panel>
      )}

      {undated.length > 0 && (
        <Panel title="Important information" icon="info-circle">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {undated.map(a => <AnnouncementCard key={a.id} item={a} />)}
          </div>
        </Panel>
      )}

      {items !== null && items.length === 0 && (
        <Panel title="Important information" icon="info-circle">
          <div style={{ color: c.subtle, fontSize: '0.87rem' }}>
            Nothing to report right now. Upcoming events and firm news will appear here.
          </div>
        </Panel>
      )}
    </div>
  );
}
Home.propTypes = { firstName: PropTypes.string, onGoToSetup: PropTypes.func };

// ── Marketing Resources ───────────────────────────────────────────────────────
// The collateral PCA publishes to every entrepreneur firm. Each category is its
// own menu item under a "Marketing Resources" group, and each item is either a
// file (opened through a short-lived presigned S3 URL) or an external link.
//
// The whole library arrives in one call at the shell — the menu needs the
// category list anyway, so fetching per-page would mean asking twice for the
// same payload and would make switching between two sections flicker.

const RESOURCE_FILE_ICONS = {
  pdf: 'file-earmark-pdf', ppt: 'file-earmark-slides', pptx: 'file-earmark-slides',
  key: 'file-earmark-slides', doc: 'file-earmark-word', docx: 'file-earmark-word',
  xls: 'file-earmark-spreadsheet', xlsx: 'file-earmark-spreadsheet',
  xlsm: 'file-earmark-spreadsheet', csv: 'file-earmark-spreadsheet',
  png: 'file-earmark-image', jpg: 'file-earmark-image', jpeg: 'file-earmark-image',
  gif: 'file-earmark-image', svg: 'file-earmark-image', webp: 'file-earmark-image',
  tif: 'file-earmark-image', tiff: 'file-earmark-image',
  zip: 'file-earmark-zip', mp4: 'file-earmark-play', mov: 'file-earmark-play',
  m4v: 'file-earmark-play',
};

const resourceIcon = (resource) => {
  if (resource.kind === 'LINK') return 'box-arrow-up-right';
  const ext = (resource.filename || '').split('.').pop()?.toLowerCase();
  return RESOURCE_FILE_ICONS[ext] || 'file-earmark';
};

const fmtBytes = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

/* Light markup for staff-authored copy: blank-line paragraphs, "- " bullets,
 * [text](url) links, **bold**. That is exactly the set the source marketing
 * pages use and no more.
 *
 * Deliberately not a markdown library. This renders copy written by PCA staff
 * onto an external firm's screen, so the safe shape is a parser that can only
 * emit <p>, <ul>, <strong> and a scheme-checked <a> — anything it does not
 * recognise comes out as literal text rather than as markup.
 *
 * Duplicated from the intranet's copy in routes/MarketingResources.js on
 * purpose: b2b/ imports nothing from the intranet, so shared code here would
 * mean the portal breaking when an intranet page is refactored. The C2P Central
 * rewrite below is the exception: it is a pure URL function in components/, not
 * intranet page code, and duplicating it would guarantee the two copies drift
 * on the one thing that has to stay identical. richtextLinkParity.test.jsx
 * checks this renderer and the intranet's agree. */
const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

function renderInline(text, keyPrefix) {
  const out = [];
  const pattern = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*/g;
  let cursor = 0;
  let n = 0;
  let match = pattern.exec(text);
  while (match !== null) {
    if (match.index > cursor) out.push(text.slice(cursor, match.index));
    if (match[1] !== undefined) {
      // An unrecognised scheme (javascript:, data:) degrades to its label
      // rather than becoming a link.
      out.push(SAFE_HREF.test(match[2]) ? (
        <a key={`${keyPrefix}-${n}`} href={c2pCentralLoginLink(match[2])} target="_blank" rel="noopener noreferrer"
          style={{ color: c.teal }}>{match[1]}</a>
      ) : match[1]);
    } else {
      out.push(<strong key={`${keyPrefix}-${n}`}>{match[3]}</strong>);
    }
    cursor = pattern.lastIndex;
    n += 1;
    match = pattern.exec(text);
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

export function RichText({ text, style }) {
  if (!text) return null;
  const blocks = [];
  let bullets = [];
  const flushBullets = () => {
    if (!bullets.length) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} style={{ margin: '0 0 0.5rem', paddingLeft: '1.15rem' }}>
        {bullets.map((line, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} style={{ marginBottom: '0.15rem' }}>{renderInline(line, `li-${blocks.length}-${i}`)}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };
  text.split('\n').forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flushBullets(); return; }
    if (line.startsWith('- ')) { bullets.push(line.slice(2)); return; }
    flushBullets();
    blocks.push(
      // eslint-disable-next-line react/no-array-index-key
      <p key={`p-${i}`} style={{ margin: '0 0 0.45rem' }}>{renderInline(line, `p-${i}`)}</p>,
    );
  });
  flushBullets();
  return <div style={{ color: c.subtle, fontSize: '0.87rem', ...style }}>{blocks}</div>;
}
RichText.propTypes = { text: PropTypes.string, style: PropTypes.object };

function MarketingCategory({ category }) {
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);
  // Which accordion groups are open. Collapsed by default so a section like the
  // Presentation Library opens as ~10 scannable headings rather than 58 rows;
  // a category flagged collapsible=false (Member Discounts) ignores this and
  // renders everything open, because there the body copy IS the page.
  const [openGroups, setOpenGroups] = useState({});
  const groups = category?.groups || [];
  const resources = category?.resources || [];
  const collapsible = category?.collapsible !== false;

  const toggleGroup = (id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  /* Everything opens through the API, files and links alike.
   *
   * For a FILE that has always been true (the presigned URL doesn't exist until
   * it's asked for). For a LINK it is now true as well, so the click can be
   * COUNTED — almost every asset is an external Box link, and a browser
   * navigating straight there is invisible to us. It also keeps the raw Box URL
   * out of the page source, where it could be copied and forwarded outside the
   * member base.
   *
   * Unlike a client document the resulting link is NOT single-use — this is
   * published collateral, identical for every firm, so a five-minute reusable
   * URL is the right trade and there is no blob-URL dance (see fetchAsBlobUrl
   * above); the browser gets its native PDF/video viewer.
   *
   * The tab is opened SYNCHRONOUSLY: after the await the browser no longer
   * treats window.open as user-initiated and Safari/Firefox silently block it.
   * `noopener` can't be a window feature here (it makes window.open return null
   * and we need the handle), so the opener is severed by hand.
   */
  const openVia = async (path, id) => {
    setBusyId(id); setErr(null);
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const res = await b2bFetch(path);
      if (res?.url) {
        if (tab) tab.location = res.url;
        else window.location.assign(res.url);
      } else {
        if (tab) tab.close();
        setErr('That item could not be opened.');
      }
    } catch (e) {
      if (tab) tab.close();
      setErr(e?.detail || e?.message || 'That item could not be opened.');
    } finally {
      setBusyId(null);
    }
  };

  const openResource = (resource) => openVia(
    resource.kind === 'LINK'
      ? `/api/b2b/marketing/resources/${resource.id}/open`
      : `/api/b2b/marketing/resources/${resource.id}/download_url`,
    resource.id,
  );

  const openGroupLink = (group) => openVia(
    `/api/b2b/marketing/groups/${group.id}/open`, `group-${group.id}`,
  );

  const rowStyle = {
    display: 'flex', alignItems: 'flex-start', gap: '0.75rem', width: '100%',
    textAlign: 'left', padding: '0.75rem 0.25rem', background: 'transparent',
    border: 'none', borderTop: `1px solid ${c.line}`, textDecoration: 'none',
    cursor: 'pointer', color: 'inherit',
  };

  const rowBody = (resource) => (
    <>
      <i className={`bi bi-${resourceIcon(resource)}`} style={{ color: c.teal, fontSize: '1.15rem', flex: '0 0 auto' }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 700, color: c.navy, fontSize: '0.9rem' }}>{resource.title}</span>
        {resource.description && (
          <span style={{ display: 'block', color: c.subtle, fontSize: '0.83rem', marginTop: '0.1rem' }}>
            {resource.description}
          </span>
        )}
        <span style={{ display: 'block', color: c.subtle, fontSize: '0.72rem', marginTop: '0.15rem' }}>
          {resource.kind === 'LINK'
            ? 'Opens in a new tab'
            : [resource.filename, fmtBytes(resource.content_size)].filter(Boolean).join(' · ')}
        </span>
      </span>
      <i className={`bi bi-${busyId === resource.id ? 'hourglass-split' : (resource.kind === 'LINK' ? 'arrow-up-right' : 'download')}`}
        style={{ color: c.subtle, flex: '0 0 auto' }} />
    </>
  );

  // One asset. A HEADING is neither a link nor a file — it is the bold run-in
  // label the source pages use to break a long presentation into chunks, so it
  // renders as a divider and is not clickable.
  const renderResource = (r) => {
    if (r.kind === 'HEADING') {
      return (
        <div key={r.id} style={{
          marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: `1px solid ${c.line}`,
          color: c.navy, fontWeight: 700, fontSize: '0.72rem',
          textTransform: 'uppercase', letterSpacing: '0.7px',
        }}>
          {r.title}
        </div>
      );
    }
    return (
      <button key={r.id} type="button" style={rowStyle} disabled={busyId === r.id}
        onClick={() => openResource(r)}>
        {rowBody(r)}
      </button>
    );
  };

  const renderGroup = (group) => {
    const items = group.resources || [];
    const open = !collapsible || !!openGroups[group.id];
    const count = items.filter((r) => r.kind !== 'HEADING').length;

    const heading = (
      <>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 700, color: c.navy, fontSize: '0.95rem' }}>
            {group.title}
            {group.badge && (
              <span style={{
                marginLeft: '0.5rem', background: 'rgba(208,155,44,0.16)', color: c.amber,
                borderRadius: 5, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700,
                letterSpacing: '0.4px', verticalAlign: 'middle',
              }}>
                {group.badge}
              </span>
            )}
          </span>
          {group.subtitle && (
            <span style={{ display: 'block', color: c.subtle, fontSize: '0.83rem', marginTop: '0.1rem' }}>
              {group.subtitle}
            </span>
          )}
        </span>
        {collapsible && (
          <span style={{ color: c.subtle, fontSize: '0.75rem', flex: '0 0 auto' }}>
            {count} item{count === 1 ? '' : 's'}
            <i className={`bi bi-chevron-${open ? 'up' : 'down'} ms-2`} />
          </span>
        )}
      </>
    );

    return (
      <div key={group.id} style={{ borderTop: `1px solid ${c.line}`, padding: '0.85rem 0' }}>
        {collapsible ? (
          <button type="button" onClick={() => toggleGroup(group.id)}
            aria-expanded={open}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%',
              textAlign: 'left', background: 'transparent', border: 'none', padding: 0,
              cursor: 'pointer', color: 'inherit',
            }}>
            {heading}
          </button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>{heading}</div>
        )}

        {open && (
          <div style={{ marginTop: '0.35rem' }}>
            <RichText text={group.body} style={{ marginTop: '0.4rem' }} />
            {/* The group's own link — the guide PDF behind a guide's title, the
                partner's site behind a partner name. Shown as a row rather than
                by making the heading itself a link, so that on a collapsible
                section the heading stays a reliable expand/collapse target. */}
            {group.url && (
              <button type="button" style={rowStyle} disabled={busyId === `group-${group.id}`}
                onClick={() => openGroupLink(group)}>
                <i className="bi bi-box-arrow-up-right" style={{ color: c.teal, fontSize: '1.15rem', flex: '0 0 auto' }} />
                <span style={{ flex: 1, minWidth: 0, fontWeight: 700, color: c.navy, fontSize: '0.9rem' }}>
                  {group.title}
                </span>
                <i className="bi bi-arrow-up-right" style={{ color: c.subtle, flex: '0 0 auto' }} />
              </button>
            )}
            {items.map(renderResource)}
            {items.length === 0 && !group.url && !group.body && (
              <p style={{ color: c.subtle, fontSize: '0.85rem', margin: '0.4rem 0 0' }}>
                Nothing published here yet.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const isEmpty = groups.length === 0 && resources.length === 0;

  return (
    <Panel title={category?.name || 'Marketing Resources'} icon={category?.icon || 'collection'}
      actions={collapsible && groups.length > 1 ? (
        <button type="button"
          onClick={() => setOpenGroups(
            Object.keys(openGroups).some((k) => openGroups[k])
              ? {}
              : Object.fromEntries(groups.map((g) => [g.id, true])),
          )}
          style={{
            background: 'rgba(255,255,255,0.12)', color: 'white', border: 'none',
            borderRadius: 6, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 600,
            cursor: 'pointer',
          }}>
          {Object.keys(openGroups).some((k) => openGroups[k]) ? 'Collapse all' : 'Expand all'}
        </button>
      ) : null}>
      {err && (
        <div style={{ background: 'rgba(154,28,28,0.06)', border: `1px solid ${c.red}`, color: c.red, borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          {err}
        </div>
      )}
      {category?.description && (
        <p style={{ color: c.subtle, fontSize: '0.87rem', margin: '0 0 0.5rem' }}>{category.description}</p>
      )}
      {/* The section's opening copy — "What's included each month", the
          Presentation Library's compliance note. */}
      <RichText text={category?.intro} style={{ marginBottom: '0.6rem' }} />

      {isEmpty && (
        <p style={{ color: c.subtle, fontSize: '0.88rem', margin: 0 }}>
          Nothing has been published in this section yet. Check back soon.
        </p>
      )}

      {/* Ungrouped items sit ABOVE the groups: they belong to the section as a
          whole (the Presentation Library's Instructions, Tracking Form and
          delivery video), so burying them under the presentations would be
          wrong — they are what you are meant to read first. */}
      {resources.map(renderResource)}

      {groups.map(renderGroup)}
    </Panel>
  );
}
MarketingCategory.propTypes = { category: PropTypes.object };

// ── Resource Library pages ────────────────────────────────────────────────────
// The other half of the Resource Library group: the internal reference pages
// that were also ticked "Visible to entrepreneurs". Two levels (page → item) against the
// marketing library's three, because these are flat lists of links and files.
//
// Four item kinds, and each one does a DIFFERENT thing on click — a document
// opens in a tab, a video expands into a player, a link is a link, a YouTube URL
// becomes an embedded player. The server decides which from what was uploaded or
// pasted; this only reads `kind`.
//
// Ported from the intranet's routes/ResourceLibrary.js rather than shared with
// it, for the reason the RichText duplication above gives: b2b/ imports nothing
// from the intranet, so the portal cannot break when an intranet page is
// refactored. What is NOT ported is the search box and the page picker — the
// portal's menu already is the page picker, and a firm sees a handful of pages
// rather than the whole internal library.

const RESOURCE_KIND_ICONS = {
  LINK: 'box-arrow-up-right',
  EMBED: 'play-btn-fill',
  VIDEO: 'play-btn-fill',
};

const resourceItemIcon = (item) => (
  RESOURCE_KIND_ICONS[item.kind]
  || RESOURCE_FILE_ICONS[(item.filename || '').split('.').pop()?.toLowerCase()]
  || 'file-earmark'
);

/* A 16:9 box that holds its shape at every width. `aspect-ratio` with
 * max-width:100%, and the child absolutely positioned so an iframe carrying its
 * own width attribute can't override it. */
function ResourcePlayerFrame({ children }) {
  return (
    <div style={{
      position: 'relative', width: '100%', maxWidth: 880, aspectRatio: '16 / 9',
      background: '#000', borderRadius: 8, overflow: 'hidden',
      margin: '0.2rem 0 0.6rem',
    }}>
      <div style={{ position: 'absolute', inset: 0 }}>{children}</div>
    </div>
  );
}
ResourcePlayerFrame.propTypes = { children: PropTypes.node };
ResourcePlayerFrame.defaultProps = { children: null };

function ResourceItemRow({ item, onError }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // The presigned URL for an uploaded video, minted the first time the row is
  // expanded and kept for the visit. Its TTL is hours rather than the five
  // minutes a download gets, precisely so it survives being watched.
  const [videoUrl, setVideoUrl] = useState(null);
  // Chrome can't play video/quicktime. Rather than guess from the content type,
  // the player reports its own failure and the row falls back to a download —
  // the only honest answer, since the file is fine and the browser simply can't
  // decode it.
  const [unplayable, setUnplayable] = useState(false);

  const { kind } = item;
  const isUpload = kind === 'FILE' || kind === 'VIDEO';
  // An upload shows its size; nothing else carries a meta line. Filename,
  // updated date and kind label are all deliberately not shown - mirrors
  // routes/ResourceLibrary.js, where the comment explains why.
  const meta = isUpload ? fmtBytes(item.content_size) : '';

  const fileUrl = async (disposition) => {
    const res = await b2bFetch(
      `/api/b2b/resource-library/items/${item.id}/file_url?disposition=${disposition}`,
    );
    return res?.url || null;
  };

  /* A document: mint a URL and open it in a new tab. The tab is opened
   * SYNCHRONOUSLY, before the await — afterwards the browser stops treating
   * window.open as user-initiated and Safari/Firefox block it silently.
   * `noopener` can't be passed as a window feature (it makes window.open return
   * null and the handle is needed), so the opener is severed by hand. Same dance
   * as openVia in the marketing section above. */
  const openFile = async (disposition) => {
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    setBusy(true);
    try {
      const url = await fileUrl(disposition);
      if (url) {
        if (tab) tab.location = url;
        else window.location.assign(url);
      } else {
        if (tab) tab.close();
        onError('That file could not be opened.');
      }
    } catch (e) {
      if (tab) tab.close();
      onError(e?.detail || e?.message || 'That file could not be opened.');
    } finally { setBusy(false); }
  };

  const toggleVideo = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (videoUrl) return;
    setBusy(true);
    try {
      const url = await fileUrl('inline');
      if (url) setVideoUrl(url);
      else onError('That video could not be loaded.');
    } catch (e) {
      onError(e?.detail || e?.message || 'That video could not be loaded.');
      setOpen(false);
    } finally { setBusy(false); }
  };

  const rowStyle = {
    display: 'flex', alignItems: 'flex-start', gap: '0.75rem', width: '100%',
    textAlign: 'left', padding: '0.75rem 0.25rem', background: 'transparent',
    border: 'none', borderTop: `1px solid ${c.line}`, textDecoration: 'none',
    cursor: 'pointer', color: 'inherit',
  };

  const label = (
    <>
      <i className={`bi bi-${resourceItemIcon(item)}`}
        style={{ color: c.teal, fontSize: '1.15rem', flex: '0 0 auto' }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 700, color: c.navy, fontSize: '0.9rem' }}>
          {item.title}
        </span>
        {item.description && (
          <span style={{ display: 'block', color: c.subtle, fontSize: '0.83rem', marginTop: '0.1rem' }}>
            {item.description}
          </span>
        )}
        {meta && (
          <span style={{ display: 'block', color: c.subtle, fontSize: '0.72rem', marginTop: '0.15rem' }}>
            {meta}
          </span>
        )}
      </span>
    </>
  );

  const chevron = (icon) => (
    <i className={`bi bi-${busy ? 'hourglass-split' : icon}`}
      style={{ color: c.subtle, flex: '0 0 auto' }} />
  );

  /* A website link: a REAL anchor, unlike every row in the marketing section
   * above. Those go through /open so the firm-engagement report can count the
   * click; these pages have no tracking at all (there is no resource-library
   * equivalent of marketing_events), so there is nothing to route through and
   * middle-click, "copy link address" and the status bar all work. */
  if (kind === 'LINK') {
    return (
      <a style={rowStyle} href={c2pCentralLoginLink(item.url)} target="_blank" rel="noopener noreferrer">
        {label}
        {chevron('arrow-up-right')}
      </a>
    );
  }

  // A YouTube/Vimeo video: expands into the provider's player. `src` is the
  // server-BUILT embed URL, never the URL someone pasted.
  if (kind === 'EMBED') {
    return (
      <div style={{ borderTop: `1px solid ${c.line}` }}>
        <button type="button" style={{ ...rowStyle, borderTop: 'none' }}
          onClick={() => setOpen(v => !v)} aria-expanded={open}>
          {label}
          {chevron(open ? 'chevron-up' : 'play-fill')}
        </button>
        {open && item.embed_url && (
          <ResourcePlayerFrame>
            <iframe
              src={item.embed_url}
              title={item.title}
              style={{ width: '100%', height: '100%', border: 'none' }}
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </ResourcePlayerFrame>
        )}
        {open && !item.embed_url && (
          // Only reachable if the row's embed_url failed the server's outbound
          // check. The link still works, so the row still does something.
          <div style={{ padding: '0 0.25rem 0.7rem' }}>
            <a href={c2pCentralLoginLink(item.url)} target="_blank" rel="noopener noreferrer"
              style={{ color: c.teal, fontSize: '0.82rem' }}>
              Open this video in a new tab
            </a>
          </div>
        )}
      </div>
    );
  }

  // An uploaded video: expands into an inline player over a presigned URL.
  if (kind === 'VIDEO') {
    return (
      <div style={{ borderTop: `1px solid ${c.line}` }}>
        <button type="button" style={{ ...rowStyle, borderTop: 'none' }}
          onClick={toggleVideo} aria-expanded={open} disabled={busy}>
          {label}
          {chevron(open ? 'chevron-up' : 'play-fill')}
        </button>
        {open && videoUrl && !unplayable && (
          <ResourcePlayerFrame>
            {/* No <track>: these are staff screen recordings with no caption
                file to point at.
                Do NOT add a lint-suppression comment for
                jsx-a11y/media-has-caption here. That rule name does not exist
                in the eslint-plugin-jsx-a11y version the production image
                installs, and a suppression naming an unknown rule is itself a
                build error there ("Definition for rule ... was not found") — it
                fails `docker build`, not just `npm test`. The rule is not
                enabled by react-app, so there is nothing to suppress. */}
            <video
              src={videoUrl}
              controls
              preload="metadata"
              onError={() => setUnplayable(true)}
              style={{ width: '100%', height: '100%', background: '#000' }}
            />
          </ResourcePlayerFrame>
        )}
        {open && unplayable && (
          <div style={{ padding: '0 0.25rem 0.7rem', color: c.subtle, fontSize: '0.82rem' }}>
            <i className="bi bi-info-circle me-1" />
            This browser can&apos;t play {item.filename ? `"${item.filename}"` : 'that format'}.
            <button type="button" onClick={() => openFile('attachment')}
              style={{
                background: 'none', border: 'none', padding: '0 0 0 0.3rem',
                color: c.teal, fontSize: '0.82rem', cursor: 'pointer',
                textDecoration: 'underline',
              }}>
              Download it instead
            </button>
          </div>
        )}
      </div>
    );
  }

  // A document.
  return (
    <button type="button" style={rowStyle} onClick={() => openFile('inline')} disabled={busy}>
      {label}
      {chevron('box-arrow-up-right')}
    </button>
  );
}
ResourceItemRow.propTypes = {
  item: PropTypes.object.isRequired,
  onError: PropTypes.func.isRequired,
};

// Exported for ResourceLibrary.smoke.test.js. Four kinds that must each do a
// DIFFERENT thing on click is exactly what looks fine in a diff and ships a page
// where a video row downloads an mp4.
export function ResourcePage({ page }) {
  const [err, setErr] = useState(null);
  const items = page?.items || [];

  return (
    <Panel title={page?.name || 'Resource Library'} icon={page?.icon || 'collection'}>
      {err && (
        <div style={{ background: 'rgba(154,28,28,0.06)', border: `1px solid ${c.red}`, color: c.red, borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          {err}
        </div>
      )}
      {page?.description && (
        <p style={{ color: c.subtle, fontSize: '0.87rem', margin: '0 0 0.5rem' }}>{page.description}</p>
      )}
      <RichText text={page?.intro} style={{ marginBottom: '0.6rem' }} />

      {items.length === 0 && (
        <p style={{ color: c.subtle, fontSize: '0.88rem', margin: 0 }}>
          Nothing has been published on this page yet. Check back soon.
        </p>
      )}

      {items.map(item => (
        <ResourceItemRow key={item.id} item={item} onError={setErr} />
      ))}
    </Panel>
  );
}
ResourcePage.propTypes = { page: PropTypes.object };

// ── New Salesforce training sign-up ───────────────────────────────────────────
// Members register for configurable training sessions. Firms know this as "New
// Salesforce"; "FSC" stays in the endpoint paths and table names, which are
// internal and not worth the churn of renaming.
function FscTraining() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);   // "date|time" currently toggling

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setData(await b2bFetch('/api/b2b/fsc-training')); }
    catch (e) { setErr(e?.message || 'Could not load training sessions.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (s) => {
    const key = `${s.session_date}|${s.session_time}`;
    setBusy(key); setErr(null);
    try {
      await b2bFetch(`/api/b2b/fsc-training/${s.registered ? 'cancel' : 'signup'}`, {
        method: 'POST',
        body: JSON.stringify({ session_date: s.session_date, session_time: s.session_time }),
      });
      await load();
    } catch (e) { setErr(e?.message || 'Could not update your registration.'); }
    finally { setBusy(null); }
  };

  const tz = data?.timezone || 'ET';
  const sessions = data?.sessions || [];
  const byDate = {};
  sessions.forEach((s) => { (byDate[s.session_date] = byDate[s.session_date] || []).push(s); });
  const months = [...new Set(Object.keys(byDate).map((d) => d.slice(0, 7)))].sort();
  const registeredCount = sessions.filter((s) => s.registered).length;
  const pad = (n) => String(n).padStart(2, '0');
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const to12 = (hm) => { let [h, mi] = hm.split(':').map(Number); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}:${pad(mi)} ${ap}`; };
  const fullSlot = (slot) => { const [a, b] = slot.split('-'); return `${to12(a)} – ${to12(b)} ${tz}`; };
  const shortSlot = (slot) => { let [h] = slot.split('-')[0].split(':').map(Number); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h} ${ap}`; };

  const renderMonth = (ym) => {
    const [y, m] = ym.split('-').map(Number);
    const startDow = new Date(y, m - 1, 1).getDay();
    const days = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i += 1) cells.push(null);
    for (let d = 1; d <= days; d += 1) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return (
      <div key={ym} style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontWeight: 700, color: c.navy, fontSize: '0.95rem', marginBottom: '0.5rem' }}>{MONTHS[m - 1]} {y}</div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 560 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
              {DOW.map((d) => (
                <div key={d} style={{ textAlign: 'center', fontSize: '0.68rem', fontWeight: 700, color: c.subtle, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{d}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
              {cells.map((d, i) => {
                if (!d) return <div key={`b${i}`} style={{ minHeight: 76 }} />;
                const date = `${y}-${pad(m)}-${pad(d)}`;
                const daySessions = (byDate[date] || []).slice().sort((a, b) => a.session_time.localeCompare(b.session_time));
                const has = daySessions.length > 0;
                return (
                  <div key={date} style={{ minHeight: 76, border: `1px solid ${c.line}`, borderRadius: 8, padding: '0.3rem', background: has ? c.surface : 'rgba(233,230,225,0.35)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: has ? c.ink : c.subtle }}>{d}</div>
                    {daySessions.map((s) => {
                      const key = `${s.session_date}|${s.session_time}`;
                      return (
                        <button
                          key={key} type="button" onClick={() => toggle(s)} disabled={busy === key}
                          title={`${fullSlot(s.session_time)} — click to ${s.registered ? 'cancel' : 'register'}`}
                          style={{
                            border: s.registered ? 'none' : `1px solid ${c.navy}`, borderRadius: 5,
                            padding: '0.12rem 0.25rem', fontSize: '0.66rem', fontWeight: 700, lineHeight: 1.3,
                            cursor: busy === key ? 'default' : 'pointer', whiteSpace: 'nowrap', textAlign: 'center',
                            background: s.registered ? c.gold : 'white', color: s.registered ? 'white' : c.navy,
                          }}
                        >
                          {busy === key ? '…' : `${s.registered ? '✓ ' : ''}${shortSlot(s.session_time)}`}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Panel title="New Salesforce Training" icon="mortarboard">
      <p style={{ color: c.ink, fontSize: '0.88rem', maxWidth: 640, marginBottom: '0.75rem' }}>
        Sign up for a New Salesforce training session. Click a time on any
        day to register — you can pick more than one, and click it again to cancel.
      </p>
      <div style={{ display: 'flex', gap: '1.1rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem', fontSize: '0.78rem', color: c.subtle }}>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: 'white', border: `1px solid ${c.navy}`, verticalAlign: 'middle', marginRight: 5 }} />Available</span>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: c.gold, verticalAlign: 'middle', marginRight: 5 }} />Registered</span>
        {registeredCount > 0 && <span style={{ color: c.navy, fontWeight: 700 }}>You&apos;re registered for {registeredCount} session{registeredCount === 1 ? '' : 's'}.</span>}
      </div>
      {err && <div style={{ color: c.red, fontSize: '0.85rem', marginBottom: '0.75rem' }}>{err}</div>}
      {loading ? (
        <div style={{ color: c.subtle, fontSize: '0.88rem' }}>Loading sessions…</div>
      ) : months.length === 0 ? (
        <div style={{ color: c.subtle, fontSize: '0.88rem' }}>No upcoming sessions are scheduled right now.</div>
      ) : (
        months.map(renderMonth)
      )}
    </Panel>
  );
}

// The portal's front door. Asks the server who this is once, then hands over to
// the transition dashboard (a firm still transitioning, whose roles the server
// has already cut down to that alone) or to the full workspace below.
export default function B2BDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!getB2BToken()) { navigate('/entrepreneur/login', { replace: true }); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await b2bFetch('/api/b2b/me');
        if (!cancelled) setMe(data);
      } catch (e) {
        if (cancelled) return;
        if (e.status === 401) navigate('/entrepreneur/login', { replace: true });
        else setErr(e.detail || e.message || 'Could not load your account.');
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  if (me?.firm_stage === 'transitioning') {
    const impersonator = getB2BImpersonator();
    // Same exits as the workspace: close the impersonation audit row
    // (best-effort), drop the B2B token, and go to sign-in or back to staff.
    const leave = async (to) => {
      if (impersonator) {
        try { await b2bFetch('/api/b2b/impersonate/return', { method: 'POST' }); }
        catch { /* best effort */ }
        clearB2BImpersonator();
      }
      clearB2BToken();
      navigate(to, { replace: true });
    };
    return (
      <TransitionWorkspace me={me} impersonator={impersonator}
        onLogout={() => leave('/entrepreneur/login')}
        onReturnToAdmin={() => leave('/entrepreneur/firms')} />
    );
  }
  if (me || err) return <B2BWorkspace me={me} err={err} />;
  return (
    <div style={{ minHeight: '100vh', background: c.canvas, padding: '3rem', textAlign: 'center' }}>
      <div className="spinner-border" style={{ color: c.teal }} />
    </div>
  );
}

function B2BWorkspace({ me, err }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('home');   // dashboard is the landing page
  const [hovered, setHovered] = useState(null);
  // Mobile menu overlay. Closed on arrival — the point of the hamburger is that
  // the page is what you see first.
  const [navOpen, setNavOpen] = useState(false);
  // Pick a page and dismiss the overlay. Every nav item goes through this rather
  // than setTab directly, or the member lands on the page they chose with the
  // menu still covering it.
  const go = (id) => { setTab(id); setNavOpen(false); };

  // Close the overlay when the viewport grows past the breakpoint. Without it,
  // opening the menu on a phone and rotating to landscape leaves `navOpen` true,
  // so rotating back re-opens a menu nobody asked for.
  useEffect(() => { if (!isMobile) setNavOpen(false); }, [isMobile]);
  // Firm-admin setup gate: null = still checking, false = show the guided
  // sign-up wizard, true = setup done → show the full workspace.
  const [setupComplete, setSetupComplete] = useState(null);
  const [firmName, setFirmName] = useState('');
  // Has any guided-setup step actually been finished? A firm that hasn't begun
  // gets no "continue" prompt — there is nothing to continue, and chasing
  // someone over work they never started reads as a fault in their account.
  const [setupStarted, setSetupStarted] = useState(false);
  // Set between "payment method saved" and the reload that follows it.
  const [finishingSetup, setFinishingSetup] = useState(false);

  const roles = me?.roles || [];
  const isFirmAdmin = roles.includes('B2B_ADMIN');
  const roleLabel = roles.map(r => ROLE_LABEL[r] || r).join(', ') || 'Standard';

  // Once we know the user is a firm admin, decide whether sign-up is finished.
  // Complete = the firm admin submitted onboarding themselves AND a payment
  // method is on file. We key "onboarding done" off `applied_at` (set only by
  // the firm's own /b2b/firm/apply) rather than `application_status !== 'DRAFT'`
  // — a PCA employee approving the firm jumps it straight to APPROVED/ACTIVE
  // without the admin ever submitting their sign-in domains, and we must still
  // walk the admin through the guided steps in that case.
  // Non-admins never see the wizard.
  useEffect(() => {
    if (!me) return;
    if (!isFirmAdmin) { setSetupComplete(true); return; }
    let cancelled = false;
    (async () => {
      // Retried once before giving up. Failing open hands a half-set-up firm the
      // entire post-setup workspace, and it persists until the user happens to
      // refresh — which is precisely how one transient 500 read as "the portal
      // thinks I already signed up for Claude". One retry costs nothing and
      // turns a blip back into the right answer instead of the wrong one.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const p = await loadSetupProgress();
          if (cancelled) return;
          setFirmName(p.firmName);
          setSetupComplete(p.onboarding && p.wallet);
          setSetupStarted(p.onboarding || p.members || p.wallet);
          return;
        } catch {
          if (cancelled) return;
          if (attempt === 0) continue;
          // Still failing. Fail open to the normal workspace rather than
          // trapping the user in a wizard we can't confirm they need.
          setSetupComplete(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [me, isFirmAdmin]);

  // Claude certification status for the sidebar indicator on the Claude
  // Certification tab. null = unknown (show nothing), true = submitted, false =
  // not yet. Re-fetched on tab change so it updates the moment the user
  // finishes the certification on that tab.
  const [certDone, setCertDone] = useState(null);
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await b2bFetch('/api/b2b/claude_access_status');
        if (!cancelled) setCertDone(!!d.submitted_at);
      } catch {
        // Must resolve to something: the menu waits on this, so leaving it
        // unknown would hang the sidebar. "Not certified" is the safe guess —
        // it shows the certification page, which is never harmful to offer.
        if (!cancelled) setCertDone(false);
      }
    })();
    return () => { cancelled = true; };
  }, [me, tab]);

  // Whether the New Salesforce training offering is enabled — drives that menu
  // item's visibility (config-driven on/off).
  const [fscEnabled, setFscEnabled] = useState(null);   // null = still asking
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await b2bFetch('/api/b2b/fsc-training');
        if (!cancelled) setFscEnabled(!!d.enabled);
      } catch { if (!cancelled) setFscEnabled(false); }
    })();
    return () => { cancelled = true; };
  }, [me]);

  // The marketing library. Fetched once at the shell because the MENU is built
  // from it — each category is a nested item — and the pages then render from
  // the same payload. [] (not null) means "asked, and there is nothing
  // published", which is why the menu can tell an empty library from a pending
  // one and hides the section for the first without flickering on the second.
  const [marketingCats, setMarketingCats] = useState(null);   // null = still asking
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await b2bFetch('/api/b2b/marketing/library');
        if (!cancelled) setMarketingCats(d?.categories || []);
      } catch { if (!cancelled) setMarketingCats([]); }
    })();
    return () => { cancelled = true; };
  }, [me]);

  // The Resource Library pages ticked "Visible to entrepreneurs". Fetched
  // at the shell for the same reason as the marketing library — the MENU is
  // built from it — and with the same null/[] convention: null is "still
  // asking", [] is "asked, nothing published to us".
  //
  // A separate call from the marketing one, not a merged payload: they are two
  // tables with two admin pages (both MARKETING-owned since 2026-09-18, but
  // still two features), and this one returns nothing at all until someone
  // ticks a box. Rolling them together
  // server-side would give both hubs one endpoint whose emptiness could mean
  // either thing.
  // One piece of state for both halves of the payload — the pages and the
  // folders they sit in — so `menuReady` below stays a single null check and the
  // menu can never be built from pages that have arrived with folders that
  // haven't. The server has already dropped any folder holding none of OUR
  // pages, so there is nothing to filter here.
  const [resourceLib, setResourceLib] = useState(null);       // null = still asking
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await b2bFetch('/api/b2b/resource-library/pages');
        if (!cancelled) setResourceLib({ pages: d?.pages || [], groups: d?.groups || [] });
      } catch { if (!cancelled) setResourceLib({ pages: [], groups: [] }); }
    })();
    return () => { cancelled = true; };
  }, [me]);
  const resourcePages = resourceLib?.pages || [];
  const resourceGroups = resourceLib?.groups || [];

  const inSetup = isFirmAdmin && setupComplete === false;
  const checkingSetup = isFirmAdmin && setupComplete === null;

  // Every menu item hangs off one of five async answers. Rendering before they
  // are all in shows a menu built from defaults and then rewrites it — which is
  // what made a brand-new firm admin briefly see the whole post-setup workspace,
  // Members and Invoices included, before it collapsed to their real three items.
  //
  // `setupComplete` was the one that bit: it starts null, and `inSetup` compares
  // with === false, so "still asking" and "setup finished" were indistinguishable
  // to the menu. Same discipline as certDone — don't answer until you know.
  const menuReady = !!me && !checkingSetup && certDone !== null && fscEnabled !== null
    && marketingCats !== null && resourceLib !== null;

  // The setup item disappears the moment sign-up completes, so anyone sitting on
  // that tab would be left looking at a page with no menu entry. Send them to
  // the dashboard instead.
  useEffect(() => {
    if (tab === 'setup' && !inSetup) setTab('home');
  }, [tab, inSetup]);

  // Set when a Prosperity admin is "logged in as" this user. Shows a banner with
  // a way back; returning just discards the B2B token — the admin's own intranet
  // session (a separate token) was never touched, so they land right back in it.
  const impersonator = getB2BImpersonator();

  // Close the impersonation_audit row (best-effort) before dropping the token,
  // so the session's end is recorded like the employee flow. No-op server-side
  // for a normal (non-impersonated) session.
  const endImpersonation = async () => {
    if (!impersonator) return;
    try { await b2bFetch('/api/b2b/impersonate/return', { method: 'POST' }); }
    catch { /* best effort — don't block the exit on it */ }
    clearB2BImpersonator();
  };

  const logout = async () => {
    await endImpersonation();
    clearB2BToken();
    navigate('/entrepreneur/login', { replace: true });
  };

  const returnToAdmin = async () => {
    await endImpersonation();
    clearB2BToken();
    navigate('/entrepreneur/firms', { replace: true });
  };

  // The two halves of the Resource Library group, built before the menu so the
  // "hide the group when it is empty" test is a single length check.
  //
  // Marketing keeps its own heading rather than having its sections spilled
  // straight into the group: there are four of them, they are one team's
  // library, and the employee hub nests them the same way.
  //
  // The reference pages now sit beside that heading in two forms, matching what
  // Marketing filed them as: a page in a folder becomes a child of that folder's
  // heading, and a page in no folder stays a leaf directly under the group,
  // which is where every one of them was before folders existed.
  //
  // FOLDERS FIRST, THEN THE LOOSE PAGES, as in the employee hub — the group
  // already opens with the Marketing heading, so the collapsible rows stay
  // contiguous and the direct links form one block underneath instead of being
  // split around headings.
  //
  // A page whose `group_id` names a folder that is not in the payload falls back
  // to the loose block. That covers a folder deleted between the two halves of a
  // read as well as a group the server withheld, and it fails towards "still
  // reachable, one level up" rather than "gone".
  const resourceGroupIds = new Set(resourceGroups.map(g => g.id));
  const resourceLeaf = page => ({ id: `resource:${page.id}`, label: page.name });
  const resourceLibraryChildren = [
    ...((marketingCats || []).length > 0 ? [{
      label: 'Marketing',
      children: (marketingCats || []).map(cat => ({
        id: `marketing:${cat.id}`, label: cat.name,
      })),
    }] : []),
    ...resourceGroups.map(group => ({
      // `key` because nothing stops Marketing naming two folders the same
      // thing, and a group entry has no `id` of its own (only leaves do — an id
      // here is the open tab). Without it two same-named folders would share a
      // React key and fold as one.
      key: `group:${group.id}`,
      label: group.name,
      children: resourcePages.filter(p => p.group_id === group.id).map(resourceLeaf),
    })),
    ...resourcePages
      .filter(p => p.group_id == null || !resourceGroupIds.has(p.group_id))
      .map(resourceLeaf),
  ];

  // The menu follows where the member actually is in their Claude journey.
  // Entries are either a leaf ({ id, label }) or a group carrying `children`,
  // nested up to three deep (see the sidebar building blocks above).
  //
  // "Documents" (FirmDocuments) is deliberately left out for now — not deleted,
  // just hidden. To bring it back, re-add { id: 'documents', label: 'Documents' };
  // the `tab === 'documents'` render branch and /b2b/documents endpoint are
  // untouched.
  // Hub membership and Claude are separate. Everything below the Dashboard is
  // the hub (training, resources, the firm's people) except the Claude group,
  // which only holds what applies to this person:
  //   - firm admin, firm not signed up yet -> "Claude Set Up" (an invitation,
  //     not a requirement - the rest of the workspace never waits on it)
  //   - anyone with a seat requested or live -> their certification, then usage
  //   - firm admin, firm signed up -> firm usage, invoices, payment method
  // A hub-only standard member sees no Claude entry at all.
  const hasSeat = (me?.provision_status || 'NONE') !== 'NONE';
  // certDone covers someone who finished certification before a seat was asked
  // for (or whose seat was later withdrawn) - their certificate stays reachable.
  const showCert = hasSeat || certDone === true;
  const claudeChildren = [
    ...(inSetup ? [{ id: 'setup', label: 'Claude Set Up' }] : []),
    // Before certification is submitted there is exactly one thing for the
    // member to do, so it leads. certDone starts null (still asking) and
    // neither form is shown until we know.
    ...(showCert && certDone === false ? [{ id: 'claude', label: 'Claude Certification' }] : []),
    ...((isFirmAdmin && !inSetup) || (!isFirmAdmin && hasSeat && certDone === true)
      ? [{ id: 'usage', label: 'Claude Usage' }] : []),
    ...(showCert && certDone === true ? [{ id: 'claude', label: 'Claude Certification' }] : []),
    ...(isFirmAdmin && !inSetup ? [
      { id: 'invoices', label: 'Invoices' },
      { id: 'wallet', label: 'Payment Method' },
    ] : []),
  ];

  const menu = [
    { id: 'home', label: 'Dashboard' },
    // New Salesforce training is open to every member, but only appears when
    // staff have toggled the offering on (config-driven).
    ...(fscEnabled ? [{ id: 'fsc', label: 'New Salesforce Training' }] : []),

    // A lone Claude page stays a plainly-named leaf (e.g. a member whose only
    // step is certification); two or more collect under a heading.
    ...(claudeChildren.length === 1 ? claudeChildren : []),
    ...(claudeChildren.length > 1 ? [{ label: 'Claude', children: claudeChildren }] : []),

    // Reference material. ONE "Resource Library" GROUP, with Marketing nested
    // inside it, matching Desk → Resource Library in the employee hub. Each
    // half disappears when it is empty, and the whole group with it: a heading
    // that unfolds onto nothing reads as broken.
    ...(resourceLibraryChildren.length > 0 ? [{
      label: 'Resource Library',
      children: resourceLibraryChildren,
    }] : []),

    // The firm's people. Always available to a firm admin - inviting the team
    // to the workspace has nothing to do with whether the firm uses Claude.
    ...(isFirmAdmin ? [{ id: 'members', label: 'Members' }] : []),
  ];

  // One entry, at whatever depth. Recursive because the menu is now three levels
  // deep in one place (Resource Library → Salesforce → How-Tos, and
  // Resource Library → Marketing → a section) and a hand-rolled second copy of
  // this block would be the thing that drifts — which is exactly what had to be
  // unpicked in the employee sidebar to add the folder level there.
  //
  // Groups sit open when one of their pages is showing — checked all the way
  // down, so the outer group unfolds for a grandchild too — and the member lands
  // looking at their page rather than at a folded heading.
  const renderNavEntry = (item, depth) => (
    item.children
      ? (
        <NavGroup key={item.key || `${depth}:${item.label}`} label={item.label} depth={depth}
          open={navHoldsTab(item, tab)}>
          {item.children.map(child => renderNavEntry(child, depth + 1))}
        </NavGroup>
      )
      : (
        <NavItem key={item.id} item={item} depth={depth}
          active={tab === item.id} hovered={hovered} setHovered={setHovered}
          onClick={() => go(item.id)} certDone={certDone} />
      )
  );

  return (
    <>
      {impersonator && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', background: c.amber, color: 'white', padding: '0.55rem 1rem', fontSize: '0.85rem', fontWeight: 600 }}>
          <i className="bi bi-incognito" style={{ fontSize: '1.05rem' }} />
          <span style={{ flex: 1, minWidth: 200 }}>
            Prosperity admin view — you&apos;re logged in as{impersonator.target_email ? <> <strong>{impersonator.target_email}</strong></> : ' this user'}.
          </span>
          <button type="button" onClick={returnToAdmin}
            style={{ background: 'white', color: c.amber, border: 'none', borderRadius: 6, padding: '0.3rem 0.8rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <i className="bi bi-arrow-left me-1" />Return to admin
          </button>
        </div>
      )}
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: isMobile ? 'column' : 'row', background: c.canvas, color: c.ink, fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      {/* Mobile top bar. The menu used to render as a full-width block above the
          content, which on a phone meant every section stacked between the
          member and the page they opened. Now it is a slim bar with a hamburger
          and the menu arrives as an overlay — the same shape as the internal hub
          (routes/Root.jsx).

          STICKY, not fixed, and inside the flow. Fixed would sit on top of the
          impersonation banner rendered above this shell, and would oblige `main`
          to reserve matching space — a second place to get the height right. In
          flow it just takes its own room and sticks when the page scrolls. */}
      {isMobile && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 1040, height: MOBILE_BAR, flex: '0 0 auto',
          background: c.navy, color: 'white', display: 'flex', alignItems: 'center',
          gap: '0.6rem', padding: '0 0.9rem',
          borderBottom: `2px solid ${c.gold}`,
        }}>
          <button type="button" onClick={() => setNavOpen(v => !v)}
            aria-label={navOpen ? 'Close menu' : 'Open menu'} aria-expanded={navOpen}
            style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.35rem', lineHeight: 1, padding: '0.25rem 0.35rem', cursor: 'pointer' }}>
            <i className={`bi bi-${navOpen ? 'x-lg' : 'list'}`} />
          </button>
          <div aria-hidden="true" style={{ width: 24, height: 24, flex: '0 0 24px', background: c.gold, WebkitMask: 'url(/pca.png) center / contain no-repeat', mask: 'url(/pca.png) center / contain no-repeat' }} />
          <div style={{ lineHeight: 1.05, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '0.88rem' }}>Prosperity</div>
            <div style={{ color: c.gold, fontWeight: 700, fontSize: '0.54rem', letterSpacing: '2px', textTransform: 'uppercase' }}>Entrepreneur</div>
          </div>
        </div>
      )}

      {/* Backdrop. Without it the only ways out of the overlay are picking an
          item or finding the hamburger again, which is how a menu ends up
          feeling like a trap. */}
      {isMobile && navOpen && (
        <div onClick={() => setNavOpen(false)} aria-hidden="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.45)', zIndex: 1050 }} />
      )}

      {/* Navy sidebar — mirrors the internal hub shell (Root.jsx). A static
          column on desktop; on mobile a full-height overlay above the backdrop,
          scrollable in case the menu outgrows a short screen. It covers the top
          bar rather than sitting below it, which is why it carries its own
          brand and close button — one less thing depending on the bar's
          height. */}
      <nav style={isMobile ? {
        position: 'fixed', top: 0, left: 0, bottom: 0, width: 262, zIndex: 1060,
        background: c.navy, color: 'white', display: navOpen ? 'flex' : 'none',
        flexDirection: 'column', overflowY: 'auto',
        boxShadow: '2px 0 16px rgba(10,41,71,0.35)',
      } : {
        flex: '0 0 232px', width: 232, background: c.navy, color: 'white',
        display: 'flex', flexDirection: 'column', position: 'sticky', top: 0,
        alignSelf: 'flex-start', minHeight: '100vh',
      }}>
        {/* The overlay covers the top bar, so it carries its own way out. */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.85rem 1.15rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <div aria-hidden="true" style={{ width: 26, height: 26, flex: '0 0 26px', background: c.gold, WebkitMask: 'url(/pca.png) center / contain no-repeat', mask: 'url(/pca.png) center / contain no-repeat' }} />
            <div style={{ lineHeight: 1.1, flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>Prosperity</div>
              <div style={{ color: c.gold, fontWeight: 700, fontSize: '0.55rem', letterSpacing: '2px', textTransform: 'uppercase' }}>Entrepreneur</div>
            </div>
            <button type="button" onClick={() => setNavOpen(false)} aria-label="Close menu"
              style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.15rem', lineHeight: 1, padding: '0.25rem', cursor: 'pointer' }}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
        )}

        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '1.1rem 1.15rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <div aria-hidden="true" style={{ width: 30, height: 30, flex: '0 0 30px', background: c.gold, WebkitMask: 'url(/pca.png) center / contain no-repeat', mask: 'url(/pca.png) center / contain no-repeat' }} />
            <div style={{ lineHeight: 1.1 }}>
              <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>Prosperity</div>
              <div style={{ color: c.gold, fontWeight: 700, fontSize: '0.58rem', letterSpacing: '2px', textTransform: 'uppercase' }}>Entrepreneur</div>
            </div>
          </div>
        )}

        {/* Menu (no icons). Built from five async answers, so it waits for
            all of them (menuReady) rather than rewriting itself on screen. */}
        <div style={{ flex: 1, padding: '0.75rem 0' }}>
          {menuReady && menu.map(item => renderNavEntry(item, 0))}
        </div>

        {/* Footer: signed-in user + sign out */}
        {me && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '0.9rem 1.15rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'white', wordBreak: 'break-word' }}>{me.username}</div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.6rem' }}>{roleLabel}</div>
            <button onClick={logout} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: 'white', borderRadius: 6, padding: '0.4rem 0.7rem', fontSize: '0.78rem', cursor: 'pointer', width: '100%' }}>
              <i className="bi bi-box-arrow-right me-1" />Sign out
            </button>
          </div>
        )}
      </nav>

      {/* Content — fills the available width; inner forms/tables keep their own
          sensible caps so nothing stretches awkwardly on very wide screens. */}
      <main style={{
        flex: 1, minWidth: 0,
        padding: isMobile ? '1rem' : '1.5rem 1.75rem',
      }}>
        {err ? (
          <div style={{ background: 'rgba(154,28,28,0.06)', border: '1px solid rgba(154,28,28,0.3)', color: c.red, borderRadius: 8, padding: '1rem' }}>{err}</div>
        ) : !menuReady ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: c.subtle }}>
            <div className="spinner-border" style={{ color: c.teal }} />
          </div>
        ) : tab === 'setup' ? (
          <div style={{ width: '100%' }}>
            {finishingSetup ? (
              <div style={{ background: c.surface, borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.1)', padding: '3rem 2rem', textAlign: 'center' }}>
                <i className="bi bi-check-circle-fill" style={{ color: c.green, fontSize: '2.5rem' }} />
                <div style={{ fontWeight: 800, color: c.navy, fontSize: '1.05rem', marginTop: '0.75rem' }}>
                  You&apos;re all set
                </div>
                <div style={{ color: c.subtle, fontSize: '0.88rem', marginTop: '0.3rem' }}>
                  Payment method saved. Refreshing your account…
                </div>
                <div className="spinner-border spinner-border-sm" style={{ color: c.teal, marginTop: '1rem' }} />
              </div>
            ) : (
              <SetupWizard
                firmName={firmName}
                onComplete={() => {
                  // Sign-up just finished, and a lot hangs off that: the firm's
                  // application-status chip, the menu (Claude pages and firm
                  // administration unlock here), the certification banner, the
                  // announcements. Re-fetching each by hand leaves whichever one
                  // we forget showing yesterday's answer — which is exactly the
                  // stale "Claude: Not started" people were seeing. The session
                  // JWT lives in localStorage, so a reload costs only a beat.
                  //
                  // The beat is deliberate: a page that reloads itself the
                  // instant a card is submitted reads as a glitch, and a payment
                  // is the worst moment to make someone wonder what happened.
                  setFinishingSetup(true);
                  window.setTimeout(() => window.location.reload(), 1400);
                }}
              />
            )}
          </div>
        ) : (
          <div style={{ width: '100%' }}>
            <FirmHeader
              firmName={me.firm_name || firmName}
              applicationStatus={me.application_status}
              domainStatus={me.domain_status}
              hasSeat={hasSeat}
            />
            {CLAUDE_TABS.includes(tab) && (
            <WorkspaceBanner
              currentTab={tab}
              applicationStatus={me.application_status}
              hasSeat={hasSeat}
              onGoToCertification={() => setTab('claude')}
            />
            )}
            {tab === 'home' && (
              <Home
                firstName={me.first_name}
                onGoToSetup={inSetup && setupStarted ? () => setTab('setup') : null}
              />
            )}
            {tab === 'usage' && (isFirmAdmin ? <FirmUsage /> : <MyUsage />)}
            {tab === 'documents' && <FirmDocuments />}
            {tab === 'claude' && <ClaudeCertification seatActive={['INVITED', 'ACTIVE'].includes(me?.provision_status)} />}
            {tab === 'members' && isFirmAdmin && <Members />}
            {tab === 'invoices' && isFirmAdmin && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <Invoices />
                <ActivationCharges />
              </div>
            )}
            {tab === 'wallet' && isFirmAdmin && <Wallet />}
            {tab === 'fsc' && <FscTraining />}
            {tab.startsWith('marketing:') && (
              <MarketingCategory
                category={(marketingCats || []).find(
                  cat => String(cat.id) === tab.slice('marketing:'.length))}
              />
            )}
            {tab.startsWith('resource:') && (
              <ResourcePage
                page={(resourcePages || []).find(
                  p => String(p.id) === tab.slice('resource:'.length))}
              />
            )}
          </div>
        )}
      </main>
    </div>
    </>
  );
}
B2BWorkspace.propTypes = { me: PropTypes.object, err: PropTypes.string };

// ── Guided sign-up ────────────────────────────────────────────────────────────
// A firm admin is walked through Onboarding → Members → Wallet before the full
// workspace unlocks. Ordered steps, each derived from real account state so the
// flow survives refreshes and resumes where the firm left off.
// Where a firm is in guided sign-up, derived from real account state so both the
// wizard and the dashboard prompt survive a refresh and resume where the firm
// left off. Deliberately shared: the shell needs to know whether any step is
// done, and a second copy of that rule would drift from the wizard's.
async function loadSetupProgress() {
  const [firm, members, billing] = await Promise.all([
    b2bFetch('/api/b2b/firm'),
    b2bFetch('/api/b2b/members'),
    b2bFetch('/api/b2b/billing'),
  ]);
  // Joining the workspace doesn't request a seat, so this step is done once at
  // least one person (the admin included) has a Claude seat asked for.
  const seatCount = (members.members || []).filter(m => (m.provision_status || 'NONE') !== 'NONE').length;
  return {
    firmName: firm.firm_name || '',
    // Onboarding counts as done only once the firm admin submitted it
    // (applied_at) — not when PCA approves the firm on their behalf.
    onboarding: !!firm.firm.applied_at,
    members: seatCount > 0,
    wallet: !!billing.has_payment_method,
  };
}

const SETUP_STEPS = [
  { id: 'onboarding', label: 'Onboarding', icon: 'building-gear' },
  { id: 'members', label: 'Claude seats', icon: 'people-fill' },
  { id: 'wallet', label: 'Payment Method', icon: 'wallet2' },
];

/* Circular numbered step indicator with connectors. Completed = gold + check,
 * active = navy ring, upcoming = muted. Any step is clickable to jump around.
 *
 * The buttons shrink (`0 1 96px`) rather than sitting at a fixed width, and the
 * connectors keep `flex: 1` so they give up space last. Four fixed 96px steps
 * plus connectors is wider than a phone, which put a horizontal scrollbar on the
 * whole wizard — the first screen a new firm admin ever sees. */
function Stepper({ steps, activeId, completed, onSelect }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', maxWidth: 560, margin: '0 auto 1.75rem' }}>
      {steps.map((s, i) => {
        const isDone = completed[s.id];
        const isActive = s.id === activeId;
        const bg = isActive ? c.navy : isDone ? c.gold : c.surface;
        const fg = isActive || isDone ? 'white' : c.subtle;
        const border = isActive ? c.navy : isDone ? c.gold : c.line;
        return (
          <React.Fragment key={s.id}>
            <button onClick={() => onSelect(s.id)} type="button"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: '0 1 96px', minWidth: 54 }}>
              <span style={{ width: 40, height: 40, borderRadius: '50%', background: bg, color: fg, border: `2px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.95rem', boxShadow: isActive ? '0 2px 10px rgba(10,41,71,0.25)' : 'none', transition: 'all 0.2s' }}>
                {isDone ? <i className="bi bi-check-lg" /> : i + 1}
              </span>
              <span style={{ fontSize: '0.75rem', fontWeight: isActive ? 700 : 600, color: isActive ? c.navy : c.subtle, textAlign: 'center', overflowWrap: 'anywhere' }}>{s.label}</span>
            </button>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: completed[steps[i].id] ? c.gold : c.line, marginTop: 19, borderRadius: 2 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
Stepper.propTypes = {
  steps: PropTypes.array.isRequired,
  activeId: PropTypes.string,
  completed: PropTypes.object,
  onSelect: PropTypes.func,
};

function SetupWizard({ firmName, onComplete }) {
  const [progress, setProgress] = useState(null);
  const [step, setStep] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProgress = useCallback(async () => {
    const p = await loadSetupProgress();
    setProgress(p);
    return p;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await loadProgress();
      if (cancelled) return;
      // Resume at the first unfinished step, evaluated in step order
      // (onboarding → members → wallet) so we never skip a step.
      setStep((SETUP_STEPS.find(s => !p[s.id]) || SETUP_STEPS[0]).id);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [loadProgress]);

  const refresh = useCallback(() => loadProgress(), [loadProgress]);

  if (loading || !progress) {
    return <div style={{ ...card, textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>;
  }

  const idx = SETUP_STEPS.findIndex(s => s.id === step);
  const isLast = idx === SETUP_STEPS.length - 1;
  const canContinue = step === 'onboarding' ? progress.onboarding : true;

  const goTo = (id) => setStep(id);
  const next = async () => {
    await refresh();
    if (!isLast) setStep(SETUP_STEPS[idx + 1].id);
  };

  return (
    <div>
      {/* Welcome the business */}
      <div style={{ background: c.navy, borderRadius: 12, padding: '1.5rem 1.75rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 3, background: c.gold }} />
        <p style={{ color: c.gold, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>Welcome to Prosperity Entrepreneur</p>
        <h2 style={{ color: 'white', fontWeight: 800, margin: 0, fontSize: '1.4rem' }}>
          {firmName ? `Set up Claude for ${firmName}` : 'Set up Claude for your firm'}
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.75)', margin: '0.4rem 0 0', fontSize: '0.88rem' }}>
          Three quick steps: your firm&apos;s details, who gets a Claude seat, and a payment method. Your Claude Usage and Invoices pages unlock once you’re done. The rest of your workspace works with or without Claude.
        </p>
      </div>

      <Stepper steps={SETUP_STEPS} activeId={step} completed={progress} onSelect={goTo} />

      {step === 'onboarding' && <Onboarding hideExplanation onSubmitted={next} onChange={refresh} />}
      {step === 'members' && <Members claudeFocus onChange={refresh} />}
      {step === 'wallet' && <Wallet onChange={async () => {
        const p = await refresh();
        if (p.wallet) onComplete();
      }} />}

      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem' }}>
        <button type="button" disabled={idx === 0} onClick={() => setStep(SETUP_STEPS[idx - 1].id)}
          style={{ background: 'transparent', border: `1px solid ${c.line}`, color: idx === 0 ? c.line : c.subtle, borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.82rem', cursor: idx === 0 ? 'default' : 'pointer' }}>
          <i className="bi bi-arrow-left me-1" />Back
        </button>
        {isLast ? (
          <button type="button" disabled={!progress.wallet} onClick={onComplete}
            style={{ background: progress.wallet ? c.green : c.line, color: 'white', border: 0, borderRadius: 6, padding: '0.6rem 1.3rem', fontWeight: 700, fontSize: '0.85rem', cursor: progress.wallet ? 'pointer' : 'default' }}>
            Finish setup<i className="bi bi-check-lg ms-1" />
          </button>
        ) : (
          <button type="button" disabled={!canContinue} onClick={next}
            style={{ background: canContinue ? c.navy : c.line, color: 'white', border: 0, borderRadius: 6, padding: '0.6rem 1.3rem', fontWeight: 700, fontSize: '0.85rem', cursor: canContinue ? 'pointer' : 'default' }}>
            Continue<i className="bi bi-arrow-right ms-1" />
          </button>
        )}
      </div>
      {step === 'onboarding' && !progress.onboarding && (
        <p style={{ color: c.subtle, fontSize: '0.75rem', textAlign: 'right', marginTop: 6 }}></p>
      )}
    </div>
  );
}
SetupWizard.propTypes = {
  firmName: PropTypes.string,
  onComplete: PropTypes.func.isRequired,
};

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthAbbr = (ym) => MONTH_ABBR[Number(ym.slice(5, 7)) - 1];
const monthFull = (ym) => `${MONTH_FULL[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;

// KPI tile for a dashboard header row — label, headline value, optional
// secondary line (a delta, a count, context).
function StatTile({ label, value, sub }) {
  return (
    <div style={{ background: c.surface, borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: '1rem 1.25rem', flex: '1 1 160px', minWidth: 150 }}>
      <div style={{ color: c.subtle, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>{label}</div>
      <div style={{ color: c.navy, fontSize: '1.6rem', fontWeight: 800, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ color: c.subtle, fontSize: '0.76rem', marginTop: 5 }}>{sub}</div>}
    </div>
  );
}
StatTile.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node.isRequired, sub: PropTypes.node };

// Trailing-months firm spend, single series (one hue — there's exactly one
// thing being measured, firm total, so no legend is needed). Rounded bar
// tops only (square at the baseline), hairline y-axis gridlines, per-bar
// hover tooltip with the full month name + exact spend.
function MonthlySpendChart({ months }) {
  const data = useMemo(() => ({
    labels: months.map((m) => monthAbbr(m.month)),
    datasets: [{
      data: months.map((m) => m.cost_usd),
      backgroundColor: c.teal,
      hoverBackgroundColor: '#004d7a',
      borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
      borderSkipped: 'bottom',
      maxBarThickness: 24,
      categoryPercentage: 0.6,
      barPercentage: 0.9,
    }],
  }), [months]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: c.navy,
        padding: 10,
        cornerRadius: 6,
        displayColors: false,
        titleFont: { weight: '700' },
        bodyFont: { weight: '600' },
        callbacks: {
          title: (items) => monthFull(months[items[0].dataIndex].month),
          label: (ctx) => usd(ctx.parsed.y),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: c.subtle, font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: c.line },
        border: { display: false },
        ticks: {
          color: c.subtle,
          font: { size: 11 },
          maxTicksLimit: 5,
          callback: (v) => `$${Number(v).toLocaleString()}`,
        },
      },
    },
  }), [months]);

  return <div style={{ height: 220 }}><Bar data={data} options={options} /></div>;
}
MonthlySpendChart.propTypes = { months: PropTypes.arrayOf(PropTypes.object).isRequired };

function FirmUsage() {
  const [month, setMonth] = useState(currentMonthValue());
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);
  const [historyErr, setHistoryErr] = useState(null);
  const [limitMsg, setLimitMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const d = await b2bFetch(`/api/b2b/usage?month=${encodeURIComponent(month)}`);
      setData(d);
    } catch (e) {
      setErr(e.detail || e.message || 'Could not load usage.');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  // Trailing 6-month trend — fetched once, independent of the month picker
  // above (which only scopes the per-member breakdown table below).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const h = await b2bFetch('/api/b2b/usage/history?months=6');
        if (!cancelled) setHistory(h);
      } catch (e) {
        if (!cancelled) setHistoryErr(e.detail || e.message || 'Could not load usage history.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Raise/lower a member's monthly Claude limit right from the usage table.
  const setLimit = async (uuid, dollars) => {
    setLimitMsg(null);
    const cents = Math.round(parseFloat(dollars) * 100);
    if (Number.isNaN(cents) || cents < 0) { setLimitMsg({ ok: false, text: 'Enter a valid limit.' }); return; }
    try {
      const res = await b2bFetch(`/api/b2b/members/${uuid}/limit`, { method: 'POST', body: JSON.stringify({ limit_cents: cents }) });
      setLimitMsg({ ok: true, text: res.message || 'Limit updated.' });
      await load();
    } catch (e) {
      setLimitMsg({ ok: false, text: e.detail || e.message || 'Could not update limit.' });
    }
  };

  const activeMembers = data?.members?.filter((m) => m.cost_usd > 0).length ?? 0;

  // Change vs. the prior month, only shown when that month is present in the
  // fetched trend window — otherwise there's nothing honest to compare against.
  const delta = useMemo(() => {
    if (!history?.months?.length || !data) return null;
    const idx = history.months.findIndex((m) => m.month === data.month);
    if (idx <= 0) return null;
    const prev = history.months[idx - 1].cost_usd;
    if (prev <= 0) return null;
    return { pct: ((data.total_cost_usd - prev) / prev) * 100 };
  }, [history, data]);

  const monthPicker = (
    <input type="month" value={month} onChange={e => setMonth(e.target.value)}
      max={currentMonthValue()}
      style={{ border: 'none', borderRadius: 6, padding: '0.3rem 0.5rem', fontSize: '0.8rem', color: c.ink, background: 'white' }} />
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.9rem', flexWrap: 'wrap', marginBottom: '1.1rem' }}>
        <StatTile
          label={`${monthFull(data?.month || month)} spend`}
          value={data ? usd(data.total_cost_usd) : '—'}
          sub={delta && (
            <span>
              <i className={`bi bi-arrow-${delta.pct >= 0 ? 'up' : 'down'}-short`} />{' '}
              {Math.abs(delta.pct).toFixed(0)}% vs prior month
            </span>
          )}
        />
        <StatTile label="Active members" value={data ? activeMembers : '—'} sub={data ? `of ${data.members.length} total` : null} />
        <StatTile label="Avg per active member" value={data ? usd(activeMembers ? data.total_cost_usd / activeMembers : 0) : '—'} />
      </div>

      {history?.configured && history.months.length > 0 && (
        <div style={{ background: c.surface, borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: '1.1rem 1.25rem', marginBottom: '1.1rem' }}>
          <h5 style={{ color: c.navy, fontWeight: 700, fontSize: '0.9rem', margin: '0 0 0.9rem' }}>
            Monthly spend — last {history.months.length} months
          </h5>
          <MonthlySpendChart months={history.months} />
        </div>
      )}
      {historyErr && <div style={{ color: c.red, fontSize: '0.8rem', marginBottom: '0.9rem' }}>{historyErr}</div>}

      <Panel title="Team Claude usage" icon="bar-chart-line-fill" actions={monthPicker}>
      <p style={{ color: c.subtle, fontSize: '0.8rem', margin: '0 0 1rem' }}>Month-to-date spend per member vs. their limit.</p>
      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>
      ) : err ? (
        <div style={{ color: c.red, fontSize: '0.85rem' }}>{err}</div>
      ) : !data?.configured ? (
        <div style={{ color: c.subtle, fontSize: '0.85rem' }}>Usage reporting isn’t available yet. Please check back once your firm is fully set up.</div>
      ) : data.members.length === 0 ? (
        <div style={{ color: c.subtle, fontSize: '0.85rem' }}>No members yet.</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: c.subtle, borderBottom: `1px solid ${c.line}` }}>
                  <th style={{ padding: '0.5rem 0.6rem' }}>Member</th>
                  <th style={{ padding: '0.5rem 0.6rem' }}>Role</th>
                  <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Spend (MTD)</th>
                  <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Limit</th>
                  <th style={{ padding: '0.5rem 0.6rem' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.members.map(m => {
                  const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
                  return (
                    <tr key={m.uuid} style={{ borderBottom: `1px solid ${c.line}` }}>
                      <td style={{ padding: '0.55rem 0.6rem' }}>
                        <div style={{ fontWeight: 600, color: c.ink }}>{name}</div>
                        <div style={{ color: c.subtle, fontSize: '0.75rem' }}>{m.email}</div>
                      </td>
                      <td style={{ padding: '0.55rem 0.6rem', color: c.subtle }}>{ROLE_LABEL[m.role] || m.role || '—'}</td>
                      <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', fontWeight: 700, color: c.navy }}>{usd(m.cost_usd)}</td>
                      <LimitCell member={m} onSave={setLimit} />
                      <td style={{ padding: '0.55rem 0.6rem' }}><StatusBadge status={m.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ padding: '0.6rem', fontWeight: 700, color: c.ink }}>Total</td>
                  <td style={{ padding: '0.6rem', textAlign: 'right', fontWeight: 800, color: c.navy }}>{usd(data.total_cost_usd)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
          <p style={{ color: c.subtle, fontSize: '0.72rem', marginTop: '0.9rem' }}>
            Edit any member’s monthly Claude spend limit right here — changes apply in Claude immediately.
          </p>
          {limitMsg && (
            <div style={{ color: limitMsg.ok ? c.teal : c.red, fontSize: '0.8rem', marginTop: '0.35rem' }}>{limitMsg.text}</div>
          )}
        </>
      )}
      </Panel>
    </div>
  );
}

// Same "Claude 101" certification steps as internal employees (see
// routes/ClaudeAccess.js) — same three steps, same completion logic, ported
// to this bundle's b2bFetch + local-state conventions since the B2B bundle
// deliberately has no intranet/RTK imports. Talks to the B2B-authed
// /api/b2b/claude_access_* endpoints (routers/b2b.py), which call the exact
// same db/claude_access.py functions the employee flow uses.
function ClaudeStepCard({ number, title, done, children }) {
  return (
    <div style={{ background: c.surface, borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden', marginBottom: '1.1rem', border: done ? `2px solid ${c.green}` : '2px solid transparent' }}>
      <div style={{ background: c.navy, padding: '0.8rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.7rem', borderBottom: `3px solid ${done ? c.green : c.gold}` }}>
        <span style={{ background: done ? c.green : c.gold, color: 'white', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.8rem', flexShrink: 0 }}>
          {done ? <i className="bi bi-check" /> : number}
        </span>
        <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>{title}</h5>
        {done && <span style={{ marginLeft: 'auto', color: c.green, background: 'rgba(31,138,84,0.15)', borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>Complete</span>}
      </div>
      <div style={{ padding: '1.1rem' }}>{children}</div>
    </div>
  );
}
ClaudeStepCard.propTypes = { number: PropTypes.string.isRequired, title: PropTypes.string.isRequired, done: PropTypes.bool.isRequired, children: PropTypes.node.isRequired };

// seatActive: the member's Claude seat is live, so the "waiting on approval"
// lines no longer apply and are dropped.
function ClaudeCertification({ seatActive }) {
  const isMobile = useIsMobile();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  const [policies, setPolicies] = useState(false);
  const [training, setTraining] = useState(false);
  const [certFile, setCertFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setLoadErr(null);
    try {
      const d = await b2bFetch('/api/b2b/claude_access_status');
      setStatus(d);
      setPolicies(!!d.policies_confirmed);
      setTraining(!!d.training_confirmed);
    } catch (e) {
      setLoadErr(e.detail || e.message || 'Could not load your certification status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const hasCert = !!status?.certificate_s3_key;
  const isSubmitted = !!status?.submitted_at;
  const allDone = policies && training && (hasCert || certFile);

  const handleCheckboxChange = async (field, value) => {
    if (field === 'policies') setPolicies(value); else setTraining(value);
    setSaving(true);
    try {
      await b2bFetch('/api/b2b/claude_access_confirm', {
        method: 'POST',
        body: JSON.stringify({
          policies_confirmed: field === 'policies' ? value : policies,
          training_confirmed: field === 'training' ? value : training,
        }),
      });
      await load();
    } catch { /* save errors are non-critical; checkbox stays toggled locally */ }
    setSaving(false);
  };

  const handleUpload = async () => {
    if (!certFile) return;
    setUploadError(null); setUploading(true);
    const formData = new FormData();
    formData.append('file', certFile);
    try {
      await b2bFetch('/api/b2b/claude_access_upload_cert', { method: 'POST', body: formData });
      setCertFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
    } catch (e) {
      setUploadError(e.detail || e.message || 'Upload failed. Please try again.');
    }
    setUploading(false);
  };

  if (loading) return <div style={{ ...card, textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>;
  if (loadErr) return <div style={{ ...card, color: c.red, fontSize: '0.85rem' }}>{loadErr}</div>;

  return (
    <div>
      <div style={{ background: c.navy, borderRadius: 12, padding: isMobile ? '1.1rem' : '1.5rem 1.75rem', marginBottom: '1.25rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 3, background: c.gold }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${c.gold}`, borderRadius: 12, padding: '0.7rem', flexShrink: 0 }}>
            <i className="bi bi-robot" style={{ color: c.gold, fontSize: '1.6rem' }} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ color: c.gold, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>Enterprise AI Initiative</p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.15rem' : undefined }}>Claude Certification</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: '0.85rem' }}>Complete the steps below to activate your Claude account.</p>
          </div>
          {isSubmitted && (
            <div style={{ marginLeft: isMobile ? 0 : 'auto', background: c.green, borderRadius: 12, padding: '0.7rem 1.1rem', textAlign: 'center', flexShrink: 0 }}>
              <i className="bi bi-patch-check-fill" style={{ color: 'white', fontSize: '1.3rem', display: 'block' }} />
              <div style={{ color: 'white', fontWeight: 700, fontSize: '0.72rem', marginTop: 4 }}>Submitted</div>
            </div>
          )}
        </div>
      </div>

      {isSubmitted && (
        <div style={{ background: c.green, borderRadius: 12, padding: '0.9rem 1.1rem', marginBottom: '1.25rem', color: 'white', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          <i className="bi bi-check-circle-fill" style={{ fontSize: '1.6rem', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: 2 }}>Submission Complete!</div>
            {!seatActive && <div style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>Your firm admin&apos;s approval will activate your Claude account.</div>}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 480px', minWidth: 0 }}>
          <div style={{ ...card, marginBottom: '1.1rem' }}>
            <h5 style={{ color: c.navy, fontWeight: 700, marginBottom: '0.65rem', fontSize: '1rem' }}>Welcome to Claude 101</h5>
            <p style={{ color: c.subtle, fontSize: '0.87rem', lineHeight: 1.6, margin: 0 }}>
              Complete the three steps below. Once all steps are verified and your firm admin approves your seat, your Claude account will be activated.
            </p>
          </div>

          <ClaudeStepCard number="1" title="Read Company AI Policies" done={policies}>
            <p style={{ color: c.subtle, fontSize: '0.87rem', lineHeight: 1.6, marginBottom: '0.9rem' }}>
              Review the firm&apos;s AI usage policies and acceptable use guidelines before proceeding.
            </p>
            <a href="https://c2p.box.com/s/qqd5vfeegz5l7jqto5y2hitoxoy2amp3" target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: c.teal, color: 'white', padding: '0.45rem 0.9rem', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: '0.82rem', marginBottom: '0.9rem' }}>
              <i className="bi bi-box-arrow-up-right" />AI Policies Document
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.8rem 1rem', background: policies ? 'rgba(31,138,84,0.06)' : 'rgba(10,41,71,0.04)', borderRadius: 8, border: `1px solid ${policies ? 'rgba(31,138,84,0.2)' : 'rgba(10,41,71,0.1)'}` }}>
              <input type="checkbox" id="b2bPoliciesCheck" checked={policies} onChange={e => handleCheckboxChange('policies', e.target.checked)} style={{ cursor: 'pointer' }} />
              <label htmlFor="b2bPoliciesCheck" style={{ fontWeight: 600, color: c.navy, cursor: 'pointer', fontSize: '0.87rem', userSelect: 'none' }}>
                I have read and agree to the company AI usage policies
              </label>
            </div>
          </ClaudeStepCard>

          <ClaudeStepCard number="2" title="Complete Claude 101 Training" done={training}>
            <p style={{ color: c.subtle, fontSize: '0.87rem', lineHeight: 1.6, marginBottom: '0.9rem' }}>
              Take the Claude 101 course on Anthropic&apos;s learning platform (Skilljar). Once finished, you&apos;ll receive a certificate to upload in Step 3.
            </p>
            <a href="https://anthropic.skilljar.com" target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: c.teal, color: 'white', padding: '0.45rem 0.9rem', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: '0.82rem', marginBottom: '0.9rem' }}>
              <i className="bi bi-box-arrow-up-right" />Open Anthropic Training Courses
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.8rem 1rem', background: training ? 'rgba(31,138,84,0.06)' : 'rgba(10,41,71,0.04)', borderRadius: 8, border: `1px solid ${training ? 'rgba(31,138,84,0.2)' : 'rgba(10,41,71,0.1)'}` }}>
              <input type="checkbox" id="b2bTrainingCheck" checked={training} onChange={e => handleCheckboxChange('training', e.target.checked)} style={{ cursor: 'pointer' }} />
              <label htmlFor="b2bTrainingCheck" style={{ fontWeight: 600, color: c.navy, cursor: 'pointer', fontSize: '0.87rem', userSelect: 'none' }}>
                I have completed the Claude 101 training course
              </label>
            </div>
          </ClaudeStepCard>

          <ClaudeStepCard number="3" title="Upload Your Completion Certificate" done={hasCert}>
            <p style={{ color: c.subtle, fontSize: '0.87rem', lineHeight: 1.6, marginBottom: '0.9rem' }}>
              Upload the certificate you received after completing the Claude 101 course. Accepted formats: PDF, JPG, PNG (max 10 MB).
            </p>
            {hasCert && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(31,138,84,0.06)', border: '1px solid rgba(31,138,84,0.2)', borderRadius: 8, padding: '0.7rem 0.9rem', marginBottom: '0.9rem' }}>
                <i className="bi bi-file-earmark-check-fill" style={{ color: c.green, fontSize: '1.05rem' }} />
                <div>
                  <div style={{ fontWeight: 700, color: c.green, fontSize: '0.82rem' }}>Certificate on file</div>
                  {status?.certificate_filename && <div style={{ color: c.subtle, fontSize: '0.78rem' }}>{status.certificate_filename}</div>}
                </div>
                <button onClick={() => { setCertFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; fileInputRef.current?.click(); }}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: c.teal, fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline' }}>
                  Replace
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => { setCertFile(e.target.files[0] || null); setUploadError(null); }}
                  style={{ fontSize: '0.85rem', border: `1px solid ${c.line}`, borderRadius: 6, padding: '0.4rem 0.55rem', width: '100%' }} />
              </div>
              <button onClick={handleUpload} disabled={!certFile || uploading} style={{
                background: certFile && !uploading ? c.teal : c.subtle, color: 'white', border: 'none', borderRadius: 8,
                padding: '0.5rem 1.1rem', fontWeight: 600, fontSize: '0.85rem', cursor: certFile && !uploading ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: '0.4rem', opacity: !certFile || uploading ? 0.65 : 1,
              }}>
                {uploading ? <><span className="spinner-border spinner-border-sm" /> Uploading…</> : <><i className="bi bi-cloud-upload" /> Upload</>}
              </button>
            </div>
            {uploadError && (
              <div style={{ marginTop: '0.7rem', color: c.red, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <i className="bi bi-exclamation-circle" /> {uploadError}
              </div>
            )}
          </ClaudeStepCard>
        </div>

        <div style={{ flex: '0 1 300px', minWidth: 260 }}>
          <div style={{ background: c.surface, borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
            <div style={{ background: c.navy, borderBottom: `3px solid ${c.gold}`, padding: '0.8rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i className="bi bi-list-check" style={{ color: c.gold }} />
              <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Your Progress</h5>
            </div>
            <div style={{ padding: '1.1rem' }}>
              {[
                { label: 'Read AI Policies', done: policies },
                { label: 'Completed Claude 101', done: training },
                { label: 'Certificate Uploaded', done: hasCert },
              ].map(({ label, done }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '0.8rem' }}>
                  <span style={{ width: '1.3rem', height: '1.3rem', borderRadius: '50%', background: done ? c.green : c.canvas, border: done ? 'none' : `2px solid ${c.subtle}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {done && <i className="bi bi-check" style={{ color: 'white', fontSize: '0.75rem', fontWeight: 800 }} />}
                  </span>
                  <span style={{ color: done ? c.navy : c.subtle, fontWeight: done ? 700 : 400, fontSize: '0.85rem' }}>{label}</span>
                </div>
              ))}
              <div style={{ borderTop: `1px solid ${c.line}`, marginTop: '0.4rem', paddingTop: '0.9rem' }}>
                {isSubmitted ? (
                  <div style={{ textAlign: 'center' }}>
                    <i className="bi bi-patch-check-fill" style={{ color: c.green, fontSize: '1.8rem' }} />
                    <p style={{ color: c.green, fontWeight: 700, margin: '0.4rem 0 0.2rem', fontSize: '0.88rem' }}>Submission Complete</p>
                    {!seatActive && <p style={{ color: c.subtle, fontSize: '0.78rem', margin: 0 }}>Waiting on your firm admin to approve your seat.</p>}
                  </div>
                ) : allDone ? (
                  <div style={{ textAlign: 'center' }}>
                    <i className="bi bi-hourglass-split" style={{ color: c.gold, fontSize: '1.4rem' }} />
                    <p style={{ color: c.subtle, fontSize: '0.82rem', margin: '0.4rem 0 0' }}>All steps complete.</p>
                  </div>
                ) : (
                  <p style={{ color: c.subtle, fontSize: '0.78rem', margin: 0, lineHeight: 1.5 }}>
                    Complete all three steps — your firm admin can then approve your Claude seat.
                  </p>
                )}
              </div>
              {saving && (
                <div style={{ marginTop: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.4rem', color: c.subtle, fontSize: '0.78rem' }}>
                  <span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} /> Saving…
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
ClaudeCertification.propTypes = { seatActive: PropTypes.bool };

function MyUsage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  // "Request more usage" — emails the firm admin; no in-app queue.
  const [reqOpen, setReqOpen] = useState(false);
  const [reqAmount, setReqAmount] = useState('');
  const [reqNote, setReqNote] = useState('');
  const [reqSent, setReqSent] = useState(false);
  const [reqErr, setReqErr] = useState(null);
  const [reqBusy, setReqBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const d = await b2bFetch('/api/b2b/me/usage');
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e.detail || e.message || 'Could not load your usage.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submitRequest = async () => {
    setReqErr(null);
    let cents = null;
    if (reqAmount.trim() !== '') {
      const dollars = parseFloat(reqAmount);
      if (!Number.isFinite(dollars) || dollars <= 0) { setReqErr('Enter a valid target amount, or leave it blank.'); return; }
      cents = Math.round(dollars * 100);
    }
    setReqBusy(true);
    try {
      await b2bFetch('/api/b2b/me/request_usage', { method: 'POST', body: JSON.stringify({ requested_cents: cents, note: reqNote.trim() || null }) });
      setReqSent(true); setReqOpen(false); setReqAmount(''); setReqNote('');
    } catch (e) {
      setReqErr(e.detail || e.message || 'Could not send your request.');
    } finally {
      setReqBusy(false);
    }
  };

  if (loading) return <div style={{ ...card, textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>;
  if (err) return <div style={{ ...card, color: c.red, fontSize: '0.85rem' }}>{err}</div>;
  if (!data?.configured) return <div style={{ ...card, color: c.subtle, fontSize: '0.85rem' }}>Usage reporting isn’t available yet.</div>;

  const pct = data.pct == null ? null : Math.min(data.pct, 100);
  const barColor = data.status === 'over' ? c.red : data.status === 'approaching' ? c.amber : c.teal;

  return (
    <Panel title="Your Claude usage" icon="speedometer2" actions={<StatusBadge status={data.status} />}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
        <span style={{ fontSize: '2rem', fontWeight: 800, color: c.navy }}>{usd(data.cost_usd)}</span>
        <span style={{ color: c.subtle, fontSize: '0.9rem' }}>of {usd(data.limit_usd)} this month</span>
      </div>
      {pct != null && (
        <div style={{ height: 10, background: c.line, borderRadius: 6, marginTop: '0.9rem', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: barColor, transition: 'width 0.3s' }} />
        </div>
      )}
      {(data.status === 'approaching' || data.status === 'over') && (
        <p style={{ color: data.status === 'over' ? c.red : c.amber, fontSize: '0.82rem', marginTop: '0.9rem' }}>
          {data.status === 'over'
            ? 'You’ve reached your monthly limit.'
            : 'You’re approaching your monthly limit.'}
        </p>
      )}

      {/* Request more usage — emails the firm admin, who can raise the limit. */}
      <div style={{ marginTop: '1rem', paddingTop: '0.9rem', borderTop: `1px solid ${c.line}` }}>
        {reqSent ? (
          <div style={{ color: c.teal, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <i className="bi bi-check-circle-fill" /> Request sent to your firm admin.
          </div>
        ) : !reqOpen ? (
          <button type="button" onClick={() => setReqOpen(true)}
            style={{ background: 'transparent', border: `1px solid ${c.teal}`, color: c.teal, borderRadius: 8, padding: '0.4rem 0.8rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
            <i className="bi bi-plus-circle me-1" />Request more usage
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.82rem', color: c.subtle }}>New limit (optional) $</span>
              <input type="number" min="0" step="1" value={reqAmount} onChange={e => setReqAmount(e.target.value)} placeholder="/ mo"
                style={{ width: 100, border: `1px solid ${c.line}`, borderRadius: 6, padding: '0.3rem 0.5rem', fontSize: '0.85rem', color: c.ink }} />
            </div>
            <textarea value={reqNote} onChange={e => setReqNote(e.target.value)} rows={2} placeholder="Why do you need more? (optional)"
              style={{ border: `1px solid ${c.line}`, borderRadius: 6, padding: '0.4rem 0.5rem', fontSize: '0.85rem', color: c.ink, resize: 'vertical' }} />
            {reqErr && <div style={{ color: c.red, fontSize: '0.78rem' }}>{reqErr}</div>}
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button type="button" onClick={submitRequest} disabled={reqBusy}
                style={{ background: c.navy, color: 'white', border: 0, borderRadius: 8, padding: '0.4rem 0.85rem', fontSize: '0.82rem', fontWeight: 700, cursor: reqBusy ? 'wait' : 'pointer' }}>
                {reqBusy ? 'Sending…' : 'Send to firm admin'}
              </button>
              <button type="button" onClick={() => { setReqOpen(false); setReqErr(null); }} disabled={reqBusy}
                style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.subtle, borderRadius: 8, padding: '0.4rem 0.7rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

// Firm-scoped client documents. The /api/b2b/documents endpoint hard-scopes to
// the caller's firm_id, so this only ever shows this firm's own client files.
// Shares the DocumentBrowser UI with the employee Work page.
function FirmDocuments() {
  const [params, setParams] = useState({ sort: 'recent' });
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  // In-app viewer state — {url: blob:..., filename} or null. Rendered via
  // DocumentViewerModal, an <iframe> over the current page instead of a new
  // browser tab, so viewing a document never leaves the authenticated app.
  const [viewer, setViewer] = useState(null);
  const closeViewer = useCallback(() => {
    setViewer(v => {
      if (v?.url) URL.revokeObjectURL(v.url);
      return null;
    });
  }, []);
  // Admin config's show_download_buttons flag — public endpoint, no auth
  // needed. Defaults to shown if the fetch fails for any reason.
  const [showDownloadButtons, setShowDownloadButtons] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await b2bFetch('/api/admin_config');
        if (!cancelled) setShowDownloadButtons(cfg?.config?.show_download_buttons !== false);
      } catch { /* keep default (shown) */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qs.append(k, v);
      });
      try {
        const d = await b2bFetch(`/api/b2b/documents?${qs.toString()}`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e.detail || e.message || 'Could not load documents.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  const onParamsChange = useCallback((patch) => setParams((prev) => ({ ...prev, ...patch })), []);
  // Takes the whole document: the shared DocumentBrowser hands over the
  // record rather than an id, since the Work side has a second kind of
  // document (Salesforce) served by a different endpoint. A B2B firm only
  // ever sees its own clients' portal uploads, so this just reads the id.
  const onDownload = useCallback(async (doc) => {
    try {
      const { url, filename } = await b2bFetch(`/api/b2b/documents/${doc.upload_id}/download_url`);
      if (!url) return;
      // Fetch the one-time link ourselves and hand the browser a blob: URL —
      // see fetchAsBlobUrl's comment for why we never navigate to it
      // directly. A hidden <a download> reliably forces a save dialog.
      const blobUrl = await fetchAsBlobUrl(url);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      window.alert('Could not generate a download link for that file.');
    }
  }, []);

  // "View" shows the file in an in-app modal (DocumentViewerModal), vs.
  // "Download" which forces a save. Fetching the one-time link ourselves and
  // handing the iframe a blob: URL — rather than a new browser tab pointed at
  // a real URL — keeps the document behind the app's own session for as long
  // as it's on screen; see fetchAsBlobUrl's comment for the underlying reason
  // we never navigate anywhere to the one-time link directly.
  const onView = useCallback(async (doc) => {
    try {
      const { url, filename } = await b2bFetch(`/api/b2b/documents/${doc.upload_id}/download_url?disposition=inline`);
      if (!url) throw new Error('No URL returned.');
      const blobUrl = await fetchAsBlobUrl(url);
      setViewer({ url: blobUrl, filename });
    } catch {
      window.alert('Could not open that file.');
    }
  }, []);

  return (
    <>
    <DocumentBrowser
      documents={data?.documents || []}
      facets={data?.facets || {}}
      total={data?.total || 0}
      filtered={data?.filtered || 0}
      loading={loading}
      error={err}
      params={params}
      onParamsChange={onParamsChange}
      onDownload={showDownloadButtons ? onDownload : undefined}
      onView={onView}
      title="Client Documents"
      subtitle="Search and browse documents uploaded by your firm's clients."
    />
    <DocumentViewerModal url={viewer?.url} filename={viewer?.filename} onClose={closeViewer} />
    </>
  );
}

// A member's Claude seat, separate from their hub membership. NONE (the
// default) means they use the hub only - no Claude seat has been asked for.
const PROVISION_STYLE = {
  NONE:      { label: 'No seat',          bg: 'rgba(88,88,91,0.08)',   fg: c.subtle },
  REQUESTED: { label: 'Seat requested',   bg: 'rgba(185,119,14,0.12)', fg: c.amber },
  APPROVED:  { label: 'Approved',         bg: 'rgba(0,105,166,0.10)',  fg: c.teal },
  INVITED:   { label: 'Invited',          bg: 'rgba(0,105,166,0.10)',  fg: c.teal },
  ACTIVE:    { label: 'Active',           bg: 'rgba(31,138,84,0.10)',  fg: c.green },
  REJECTED:  { label: 'Rejected',         bg: 'rgba(154,28,28,0.10)',  fg: c.red },
  REVOKED:   { label: 'Revoked',          bg: 'rgba(88,88,91,0.08)',   fg: c.subtle },
};

function ProvisionBadge({ status }) {
  const s = PROVISION_STYLE[status] || PROVISION_STYLE.NONE;
  return (
    <span style={{ background: s.bg, color: s.fg, borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>
      {s.label}
    </span>
  );
}
ProvisionBadge.propTypes = { status: PropTypes.string };

function LimitCell({ member, onSave }) {
  const [val, setVal] = useState(String(Math.round((member.limit_cents || 0) / 100)));
  useEffect(() => { setVal(String(Math.round((member.limit_cents || 0) / 100))); }, [member.limit_cents]);
  return (
    <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
        <span style={{ color: c.subtle }}>$</span>
        <input type="number" min="0" value={val} onChange={e => setVal(e.target.value)}
          style={{ width: 66, border: `1px solid ${c.line}`, borderRadius: 6, padding: '0.25rem 0.4rem', fontSize: '0.8rem', textAlign: 'right', color: c.ink }} />
        <button onClick={() => onSave(member.uuid, val)}
          style={{ background: 'transparent', border: `1px solid ${c.line}`, borderRadius: 6, padding: '0.25rem 0.5rem', fontSize: '0.72rem', cursor: 'pointer', color: c.teal }}>Save</button>
      </div>
    </td>
  );
}
LimitCell.propTypes = { member: PropTypes.object, onSave: PropTypes.func };

// The firm's people. Adding someone gives them the hub (announcements,
// resources, training); a Claude seat is a separate, optional ask per person.
// `claudeFocus` is set when this renders inside Claude Set Up, where the job
// is choosing who gets a seat rather than inviting the team.
function Members({ onChange, claudeFocus = false } = {}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const blankForm = { email: '', first_name: '', last_name: '', send_email: true, request_claude: claudeFocus };
  const [form, setForm] = useState(blankForm);
  const [seatBusy, setSeatBusy] = useState(null);
  // Seats can only be asked for once the firm has applied for Claude (that's
  // where its Claude email domains come from).
  const claudeSignedUp = !!data?.claude_signed_up;
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);
  // One-time reveal after adding a member — always returned (not just when
  // the email is skipped/fails) so it can be copied as a backup either way.
  const [addResult, setAddResult] = useState(null); // { email, temp_password, email_sent, email_error }
  const [addCopied, setAddCopied] = useState(false);
  // Which member's inline "reset password?" confirm row is open, and the
  // one-time result banner (temp password) after a reset completes.
  const [resetUuid, setResetUuid] = useState(null);
  const [resetSendEmail, setResetSendEmail] = useState(true);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null); // { email, temp_password, email_sent, email_error }
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      setData(await b2bFetch('/api/b2b/members'));
    } catch (e) {
      setErr(e.detail || e.message || 'Could not load members.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMember = async (e) => {
    e.preventDefault();
    setNotice(null); setErr(null); setAddResult(null);
    const email = form.email.trim();
    if (!email) { setErr('Enter an email address.'); return; }
    setSubmitting(true);
    try {
      const wantsSeat = claudeSignedUp && form.request_claude;
      // Same price confirmation as the row's "Request seat" button, except
      // inside Claude Set Up where the price is already on screen.
      if (wantsSeat && !claudeFocus) {
        const fee = usd((data?.annual_seat_fee_cents ?? 20000) / 100);
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Add ${email} and request a Claude seat?\n\nEach seat is ${fee} per user, per year (first year prorated), plus your team's monthly Claude usage.`)) return;
      }
      const res = await b2bFetch('/api/b2b/members', { method: 'POST', body: JSON.stringify({ ...form, request_claude: wantsSeat }) });
      setForm(blankForm);
      setAddResult({ email, temp_password: res.temp_password, email_sent: res.email_sent, email_error: res.email_error });
      setNotice(wantsSeat
        ? `Added ${email} and requested a Claude seat. They'll need to complete Claude certification, then Prosperity Capital Advisors adds them to Claude.`
        : `Added ${email} to your workspace.`);
      await load();
      if (onChange) await onChange();
    } catch (e2) {
      setErr(e2.detail || e2.message || 'Could not add member.');
    } finally {
      setSubmitting(false);
    }
  };

  const requestSeat = async (m) => {
    // eslint-disable-next-line no-alert
    const fee = usd((data?.annual_seat_fee_cents ?? 20000) / 100);
    if (!window.confirm(`Request a Claude seat for ${m.email}?\n\nEach seat is ${fee} per user, per year (first year prorated), plus your team's monthly Claude usage. They'll be asked to complete Claude certification first.`)) return;
    setSeatBusy(m.uuid); setErr(null); setNotice(null);
    try {
      await b2bFetch(`/api/b2b/members/${m.uuid}/claude_seat`, { method: 'POST' });
      setNotice(`Requested a Claude seat for ${m.email}.`);
      await load();
      if (onChange) await onChange();
    } catch (e) {
      setErr(e.detail || e.message || 'Could not request the seat.');
    } finally {
      setSeatBusy(null);
    }
  };

  const withdrawSeat = async (m) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Withdraw the Claude seat request for ${m.email}? They keep their workspace login.`)) return;
    setSeatBusy(m.uuid); setErr(null); setNotice(null);
    try {
      await b2bFetch(`/api/b2b/members/${m.uuid}/claude_seat`, { method: 'DELETE' });
      setNotice(`Withdrew the Claude seat request for ${m.email}.`);
      await load();
      if (onChange) await onChange();
    } catch (e) {
      setErr(e.detail || e.message || 'Could not withdraw the request.');
    } finally {
      setSeatBusy(null);
    }
  };

  const removeMember = async (uuid, email, hasLiveSeat) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(hasLiveSeat
      ? `Remove ${email}? This deactivates their workspace login and revokes their Claude access.`
      : `Remove ${email}? This deactivates their workspace login.`)) return;
    try {
      const res = await b2bFetch(`/api/b2b/members/${uuid}`, { method: 'DELETE' });
      setNotice(res?.revoke_note || `Removed ${email}.`);
      await load();
      if (onChange) await onChange();
    } catch (e) {
      setErr(e.detail || e.message || 'Could not remove member.');
    }
  };

  const openReset = (uuid) => {
    setResetUuid(uuid);
    setResetSendEmail(true);
    setResetResult(null);
    setErr(null);
  };

  const submitReset = async (uuid, email) => {
    setResetBusy(true); setErr(null);
    try {
      const res = await b2bFetch(`/api/b2b/members/${uuid}/reset_password?send_email=${resetSendEmail}`, { method: 'POST' });
      setResetResult({ email, temp_password: res.temp_password, email_sent: res.email_sent, email_error: res.email_error });
      setResetUuid(null);
      await load();
    } catch (e) {
      setErr(e.detail || e.message || 'Could not reset the password.');
    } finally {
      setResetBusy(false);
    }
  };

  const copyTempPassword = async () => {
    try {
      await navigator.clipboard.writeText(resetResult.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — the password is still shown on screen */ }
  };

  const copyAddedPassword = async () => {
    try {
      await navigator.clipboard.writeText(addResult.temp_password);
      setAddCopied(true);
      setTimeout(() => setAddCopied(false), 1500);
    } catch { /* clipboard unavailable — the password is still shown on screen */ }
  };

  const viewCertificate = async (uuid) => {
    try {
      const res = await b2bFetch(`/api/b2b/members/${uuid}/claude_cert_url`);
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(e.detail || e.message || 'Could not open that certificate.');
    }
  };

  const inputS = { border: `1px solid ${c.line}`, borderRadius: 6, padding: '0.5rem 0.65rem', fontSize: '0.85rem', color: c.ink };

  return (
    <Panel title={claudeFocus ? 'Claude seats' : 'Team members'} icon="people-fill">
      <p style={{ color: c.subtle, fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
        {claudeFocus
          ? 'Choose who at your firm gets a Claude seat. Anyone already in your workspace can be given a seat below, or add someone new. Each seat is reviewed by Prosperity Capital Advisors before the person is invited to Claude.'
          : 'Add the people at your firm to your Prosperity workspace. Everyone gets announcements, resources and training. A Claude seat is optional - request one only for the people who need Claude.'}
      </p>
      {/* Seat pricing belongs to the Claude flow, not to managing the team: it
          shows inside Claude Set Up, and on this everyday page the price is in
          the confirm a firm admin sees when they actually request a seat. */}
      {claudeSignedUp && claudeFocus && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: 'rgba(208,155,44,0.10)', border: `1px solid ${c.gold}`, borderRadius: 8, padding: '0.7rem 0.85rem', marginBottom: '1rem' }}>
          <i className="bi bi-info-circle-fill" style={{ color: c.amber, marginTop: 2 }} />
          <span style={{ color: c.ink, fontSize: '0.82rem', lineHeight: 1.5 }}>
            Each Claude seat is <strong>{usd((data?.annual_seat_fee_cents ?? 20000) / 100)} per user, per year</strong> (billed annually) - in addition to your team’s monthly Claude usage charges. First year fees will be prorated based on the time of entry into the Prosperity current year. Workspace members without a seat are free.
          </span>
        </div>
      )}

      <form onSubmit={addMember} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
        <input type="email" placeholder="email@yourfirm.com" value={form.email} disabled={submitting}
          onChange={e => setForm({ ...form, email: e.target.value })} style={{ ...inputS, minWidth: 220 }} />
        <input placeholder="First name" value={form.first_name} disabled={submitting}
          onChange={e => setForm({ ...form, first_name: e.target.value })} style={{ ...inputS, width: 130 }} />
        <input placeholder="Last name" value={form.last_name} disabled={submitting}
          onChange={e => setForm({ ...form, last_name: e.target.value })} style={{ ...inputS, width: 130 }} />
        <button type="submit" disabled={submitting} style={{ background: c.navy, color: 'white', border: 0, borderRadius: 6, padding: '0.55rem 1rem', fontWeight: 700, fontSize: '0.82rem', cursor: submitting ? 'wait' : 'pointer' }}>
          {submitting ? 'Adding…' : 'Add member'}
        </button>
      </form>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: '1rem' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: c.subtle, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.send_email} disabled={submitting} onChange={e => setForm({ ...form, send_email: e.target.checked })} />
          Email them their temporary password + login instructions
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: claudeSignedUp ? c.subtle : c.line, cursor: claudeSignedUp ? 'pointer' : 'default' }}
          title={claudeSignedUp ? undefined : 'Available once your firm completes Claude Set Up'}>
          <input type="checkbox" checked={claudeSignedUp && form.request_claude} disabled={submitting || !claudeSignedUp} onChange={e => setForm({ ...form, request_claude: e.target.checked })} />
          Also request a Claude seat for them
          {claudeSignedUp && data?.email_domains?.length > 0 && (
            <span style={{ color: c.subtle }}>({data.email_domains.map(d => `@${d}`).join(', ')} addresses only)</span>
          )}
          {!claudeSignedUp && <span>- available once your firm completes Claude Set Up</span>}
        </label>
      </div>

      {addResult && (
        <div style={{ background: 'rgba(31,138,84,0.08)', border: `1px solid rgba(31,138,84,0.3)`, borderRadius: 6, padding: '0.75rem 0.9rem', marginBottom: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <i className="bi bi-check-circle-fill" style={{ color: c.green }} />
            <strong style={{ color: c.ink, fontSize: '0.85rem' }}>Added {addResult.email}</strong>
            <button onClick={() => setAddResult(null)} aria-label="Dismiss" style={{ marginLeft: 'auto', background: 'transparent', border: 0, color: c.subtle, cursor: 'pointer' }}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div style={{ fontSize: '0.8rem', color: c.subtle, marginBottom: 6 }}>
            {addResult.email_sent
              ? 'Login instructions were emailed.'
              : addResult.email_error
                ? `Email failed: ${addResult.email_error} — share the password manually.`
                : 'Copy the temp password below (not emailed).'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code style={{ background: 'white', border: `1px solid ${c.line}`, borderRadius: 4, padding: '0.3rem 0.6rem', fontWeight: 700, letterSpacing: '0.03em', color: c.ink }}>
              {addResult.temp_password}
            </code>
            <button onClick={copyAddedPassword} style={{ background: addCopied ? c.green : c.teal, color: 'white', border: 0, borderRadius: 4, padding: '0.3rem 0.65rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>
              <i className={`bi bi-${addCopied ? 'check-lg' : 'clipboard'} me-1`} />{addCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ fontSize: '0.72rem', color: c.subtle, marginTop: 6 }}>Only shown once — reload this page and it&apos;s gone.</div>
        </div>
      )}

      {notice && <div style={{ background: 'rgba(0,105,166,0.08)', border: `1px solid rgba(0,105,166,0.3)`, color: c.teal, borderRadius: 6, padding: '0.6rem 0.85rem', fontSize: '0.82rem', marginBottom: '0.85rem' }}>{notice}</div>}
      {err && <div style={{ color: c.red, fontSize: '0.82rem', marginBottom: '0.85rem' }}>{err}</div>}

      {resetResult && (
        <div style={{ background: 'rgba(31,138,84,0.08)', border: `1px solid rgba(31,138,84,0.3)`, borderRadius: 6, padding: '0.75rem 0.9rem', marginBottom: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <i className="bi bi-check-circle-fill" style={{ color: c.green }} />
            <strong style={{ color: c.ink, fontSize: '0.85rem' }}>Password reset for {resetResult.email}</strong>
            <button onClick={() => setResetResult(null)} aria-label="Dismiss" style={{ marginLeft: 'auto', background: 'transparent', border: 0, color: c.subtle, cursor: 'pointer' }}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div style={{ fontSize: '0.8rem', color: c.subtle, marginBottom: 6 }}>
            {resetResult.email_sent
              ? 'Login instructions were emailed.'
              : resetResult.email_error
                ? `Email failed: ${resetResult.email_error} — share the password manually.`
                : 'Copy the temp password below (not emailed).'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code style={{ background: 'white', border: `1px solid ${c.line}`, borderRadius: 4, padding: '0.3rem 0.6rem', fontWeight: 700, letterSpacing: '0.03em', color: c.ink }}>
              {resetResult.temp_password}
            </code>
            <button onClick={copyTempPassword} style={{ background: copied ? c.green : c.teal, color: 'white', border: 0, borderRadius: 4, padding: '0.3rem 0.65rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>
              <i className={`bi bi-${copied ? 'check-lg' : 'clipboard'} me-1`} />{copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ fontSize: '0.72rem', color: c.subtle, marginTop: 6 }}>
            Only shown once — reload this page and it&apos;s gone.
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: '1.5rem', textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>
      ) : !data?.members?.length ? (
        <div style={{ color: c.subtle, fontSize: '0.85rem' }}>No members yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: c.subtle, borderBottom: `1px solid ${c.line}` }}>
                <th style={{ padding: '0.5rem 0.6rem' }}>Member</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Role</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Claude seat</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Claude cert</th>
                <th style={{ padding: '0.5rem 0.6rem' }} />
              </tr>
            </thead>
            <tbody>
              {data.members.map(m => {
                const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
                const seat = m.provision_status || 'NONE';
                const canRequest = claudeSignedUp && (seat === 'NONE' || seat === 'REJECTED');
                const canWithdraw = seat === 'REQUESTED' || seat === 'APPROVED';
                const liveSeat = seat === 'INVITED' || seat === 'ACTIVE';
                return (
                  <tr key={m.uuid} style={{ borderBottom: `1px solid ${c.line}` }}>
                    <td style={{ padding: '0.55rem 0.6rem' }}>
                      <div style={{ fontWeight: 600, color: c.ink }}>{name}</div>
                      <div style={{ color: c.subtle, fontSize: '0.75rem' }}>{m.email}</div>
                    </td>
                    <td style={{ padding: '0.55rem 0.6rem', color: c.subtle }}>{ROLE_LABEL[m.role] || m.role || '—'}</td>
                    <td style={{ padding: '0.55rem 0.6rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <ProvisionBadge status={seat} />
                        {canRequest && (
                          <button onClick={() => requestSeat(m)} disabled={seatBusy === m.uuid} style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.teal, borderRadius: 6, padding: '0.15rem 0.5rem', fontSize: '0.7rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            <i className="bi bi-plus-circle me-1" />Request seat
                          </button>
                        )}
                        {canWithdraw && (
                          <button onClick={() => withdrawSeat(m)} disabled={seatBusy === m.uuid} style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.subtle, borderRadius: 6, padding: '0.15rem 0.5rem', fontSize: '0.7rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            Withdraw
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.55rem 0.6rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {seat === 'NONE' && !m.claude_certified ? (
                          <span style={{ color: c.subtle, fontSize: '0.75rem' }}>-</span>
                        ) : m.claude_certified ? (
                          <span style={{ background: 'rgba(31,138,84,0.12)', color: c.green, borderRadius: 20, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            <i className="bi bi-patch-check-fill me-1" />Certified
                          </span>
                        ) : (
                          <span style={{ background: 'rgba(185,119,14,0.12)', color: c.amber, borderRadius: 20, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            Not yet
                          </span>
                        )}
                        {m.has_certificate && (
                          <button onClick={() => viewCertificate(m.uuid)} title="Open the uploaded certificate" style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.teal, borderRadius: 6, padding: '0.15rem 0.5rem', fontSize: '0.7rem', cursor: 'pointer' }}>
                            <i className="bi bi-file-earmark-check me-1" />View
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right' }}>
                      {resetUuid === m.uuid ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: c.subtle, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            <input type="checkbox" checked={resetSendEmail} onChange={e => setResetSendEmail(e.target.checked)} disabled={resetBusy} />
                            Email it
                          </label>
                          <button onClick={() => submitReset(m.uuid, m.email)} disabled={resetBusy} style={{ background: c.teal, color: 'white', border: 0, borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.75rem', fontWeight: 700, cursor: resetBusy ? 'wait' : 'pointer' }}>
                            {resetBusy ? 'Resetting…' : 'Confirm'}
                          </button>
                          <button onClick={() => setResetUuid(null)} disabled={resetBusy} style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.subtle, borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      ) : (
                        <>
                          <button onClick={() => openReset(m.uuid)} title="Reset password" style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.teal, borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer', marginRight: 6 }}>
                            <i className="bi bi-key me-1" />Reset password
                          </button>
                          {m.role !== 'B2B_ADMIN' && (
                            <button onClick={() => removeMember(m.uuid, m.email, liveSeat)} style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.red, borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer' }}>Remove</button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
Members.propTypes = { onChange: PropTypes.func, claudeFocus: PropTypes.bool };

const INVOICE_STYLE = {
  PAID:   { label: 'Paid',   bg: 'rgba(31,138,84,0.10)',  fg: c.green },
  FAILED: { label: 'Failed', bg: 'rgba(154,28,28,0.10)',  fg: c.red },
  DRAFT:  { label: 'Draft',  bg: 'rgba(88,88,91,0.08)',   fg: c.subtle },
  VOID:   { label: 'No charge', bg: 'rgba(88,88,91,0.08)', fg: c.subtle },
  REFUNDED: { label: 'Refunded', bg: 'rgba(37,99,235,0.10)', fg: c.navy },
};

function Invoices() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const d = await b2bFetch('/api/b2b/invoices');
        if (!cancelled) setRows(d);
      } catch (e) {
        if (!cancelled) setErr(e.detail || e.message || 'Could not load invoices.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div style={{ ...card, textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>;
  if (err) return <div style={{ ...card, color: c.red, fontSize: '0.85rem' }}>{err}</div>;

  return (
    <Panel title="Billing history" icon="receipt">
      {!rows?.length ? (
        <div style={{ color: c.subtle, fontSize: '0.85rem' }}>No invoices yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: c.subtle, borderBottom: `1px solid ${c.line}` }}>
                <th style={{ padding: '0.5rem 0.6rem' }}>Period</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Usage</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Fee</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Status</th>
                <th style={{ padding: '0.5rem 0.6rem' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map(inv => {
                const st = INVOICE_STYLE[inv.status] || INVOICE_STYLE.DRAFT;
                const isOpen = open === inv.id;
                return (
                  <React.Fragment key={inv.id}>
                    <tr style={{ borderBottom: `1px solid ${c.line}` }}>
                      <td style={{ padding: '0.55rem 0.6rem', color: c.ink, fontWeight: 600 }}>
                        {inv.period_year}-{String(inv.period_month).padStart(2, '0')}
                        {inv.combined_charge && (
                          // Several months paid in ONE card charge: show that
                          // single amount so it ties to the card statement.
                          <div style={{ fontSize: '0.7rem', fontWeight: 400, color: c.teal, marginTop: 2 }}>
                            {inv.status === 'FAILED' ? 'Combined attempt' : 'Paid in one charge'} of {usd(inv.combined_charge.total_cents / 100)}
                            {inv.combined_charge.charged_at ? ` on ${new Date(inv.combined_charge.charged_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}` : ''}
                            {' '}for {inv.combined_charge.periods.join(', ')}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', color: c.subtle }}>{usd(inv.usage_cost_cents / 100)}</td>
                      <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', color: c.subtle }}>{usd(inv.fee_cents / 100)}</td>
                      <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', fontWeight: 700, color: c.navy }}>{usd(inv.total_charged_cents / 100)}</td>
                      <td style={{ padding: '0.55rem 0.6rem' }}>
                        <span style={{ background: st.bg, color: st.fg, borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>{st.label}</span>
                      </td>
                      <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right' }}>
                        {inv.line_items?.length > 0 && (
                          <button onClick={() => setOpen(isOpen ? null : inv.id)} style={{ background: 'transparent', border: 'none', color: c.teal, cursor: 'pointer', fontSize: '0.78rem' }}>
                            {isOpen ? 'Hide' : 'Details'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isOpen && inv.line_items?.map((li, i) => (
                      <tr key={i} style={{ background: c.canvas }}>
                        <td style={{ padding: '0.35rem 0.6rem 0.35rem 1.5rem', color: c.subtle, fontSize: '0.8rem' }} colSpan={4}>{li.email}</td>
                        <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right', color: c.subtle, fontSize: '0.8rem' }} colSpan={2}>{usd(li.cost_cents / 100)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// One-time charge for each member seat added mid-cycle (Claude's prorated
// console charge + tax + our Stripe processing-fee pass-through) — separate
// from the monthly usage invoice above since it's per-member and one-off, not
// tied to a billing period.
function ActivationCharges() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const d = await b2bFetch('/api/b2b/activation-invoices');
        if (!cancelled) setRows(d);
      } catch (e) {
        if (!cancelled) setErr(e.detail || e.message || 'Could not load seat activation charges.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div style={{ ...card, textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>;
  if (err) return <div style={{ ...card, color: c.red, fontSize: '0.85rem' }}>{err}</div>;

  return (
    <Panel title="Seat activation charges" icon="person-plus">
      {!rows?.length ? (
        <div style={{ color: c.subtle, fontSize: '0.85rem' }}>No seat activation charges yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: c.subtle, borderBottom: `1px solid ${c.line}` }}>
                <th style={{ padding: '0.5rem 0.6rem' }}>Member</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Date</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Base</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Tax</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Fee</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(inv => {
                const st = INVOICE_STYLE[inv.status] || INVOICE_STYLE.DRAFT;
                return (
                  <tr key={inv.id} style={{ borderBottom: `1px solid ${c.line}` }}>
                    <td style={{ padding: '0.55rem 0.6rem', color: c.ink, fontWeight: 600 }}>{inv.member_email || '—'}</td>
                    <td style={{ padding: '0.55rem 0.6rem', color: c.subtle }}>{new Date(inv.created).toLocaleDateString()}</td>
                    <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', color: c.subtle }}>{usd(inv.base_cents / 100)}</td>
                    <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', color: c.subtle }}>{usd(inv.tax_cents / 100)}</td>
                    <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', color: c.subtle }}>{usd(inv.fee_cents / 100)}</td>
                    <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', fontWeight: 700, color: c.navy }}>{usd(inv.total_charged_cents / 100)}</td>
                    <td style={{ padding: '0.55rem 0.6rem' }}>
                      <span style={{ background: st.bg, color: st.fg, borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>{st.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// Card fields + consent, rendered inside a Stripe <Elements> provider (so the
// useStripe/useElements hooks resolve). Which methods appear (card only, vs. the
// ACH "Bank" tab) is controlled entirely server-side by the SetupIntent's
// payment_method_types — see stripe_gateway._payment_method_types (ACH is behind
// the STRIPE_ENABLE_ACH flag, off by default).
function CardFields({ billingEmail, onSaved, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    if (!name.trim()) { setErr('Please enter the name on the card.'); return; }
    if (!consent) { setErr('Please authorize automatic monthly charges.'); return; }
    if (!stripe || !elements) { setErr('Payment form isn’t ready yet.'); return; }
    setSaving(true);
    try {
      // Confirm the SetupIntent client-side; redirect: 'if_required' keeps cards
      // inline (no redirect) and returns the SetupIntent with its payment method.
      // The Payment Element opts out of collecting name/email (fields.billingDetails
      // .{name,email} = 'never', to hide Link's sign-up), so Stripe requires us to
      // pass both here in confirmParams — the name is stamped on the vaulted method.
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: { payment_method_data: { billing_details: { name: name.trim(), email: billingEmail || undefined } } },
      });
      if (error) { setErr(error.message || 'Could not save the payment method.'); return; }
      await b2bFetch('/api/b2b/billing/payment-method', {
        method: 'POST',
        body: JSON.stringify({ setup_intent_id: setupIntent.id, authorize_autocharge: true }),
      });
      setConsent(false);
      await onSaved('Payment method saved. Monthly usage will be charged automatically.');
    } catch (e) {
      setErr(e.detail || e.message || 'Could not save the payment method.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Name on the card — collected by us (not the Payment Element) so we can
          stamp it on the vaulted method's billing_details. Requires name = 'never'
          in the element's fields below, else Stripe rejects passing it in confirm. */}
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: c.ink, marginBottom: '0.35rem' }}>
        Name on card
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Jane Smith"
          autoComplete="cc-name"
          style={{ display: 'block', width: '100%', marginTop: 4, marginBottom: '0.75rem', padding: '0.55rem 0.7rem', border: `1px solid ${c.line}`, borderRadius: 6, fontSize: '0.9rem' }}
        />
      </label>
      {/* Card only. wallets.link = 'never' disables Stripe Link (the inline sign-up
          box); we also opt out of name/email collection here and pass them via
          confirmParams instead (name above; email is the firm's billing contact). */}
      <PaymentElement options={{ wallets: { link: 'never' }, fields: { billingDetails: { name: 'never', email: 'never' } } }} />
      {err && <div style={{ color: c.red, fontSize: '0.82rem', marginTop: '0.6rem' }}>{err}</div>}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '0.75rem 0', fontSize: '0.82rem', color: c.ink }}>
        <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 3 }} />
        <span>I authorize Prosperity Capital Advisors to automatically charge this payment method each month for our team’s Claude usage.</span>
      </label>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={submit} disabled={saving || !stripe} style={{ background: c.navy, color: 'white', border: 0, borderRadius: 6, padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.82rem', cursor: saving ? 'wait' : 'pointer' }}>
          {saving ? 'Saving…' : 'Save payment method'}
        </button>
        {onCancel && (
          <button onClick={onCancel} style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.subtle, borderRadius: 6, padding: '0.6rem 1rem', fontSize: '0.82rem', cursor: 'pointer' }}>Cancel</button>
        )}
      </div>
    </div>
  );
}

CardFields.propTypes = {
  billingEmail: PropTypes.string,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

// Loads a SetupIntent and mounts the Payment Element under <Elements>. Keyed on
// the publishable key so Stripe.js loads once.
function CardForm({ publishableKey, billingEmail, onSaved, onCancel }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [err, setErr] = useState(null);
  const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { client_secret: cs } = await b2bFetch('/api/b2b/billing/setup-intent');
        if (!cancelled) setClientSecret(cs);
      } catch (e) {
        if (!cancelled) setErr(e.detail || e.message || 'Could not start the payment form.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) return <div style={{ color: c.red, fontSize: '0.82rem' }}>{err}</div>;
  if (!stripePromise || !clientSecret) return <div style={{ textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>;

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
      <CardFields billingEmail={billingEmail} onSaved={onSaved} onCancel={onCancel} />
    </Elements>
  );
}

CardForm.propTypes = {
  publishableKey: PropTypes.string,
  billingEmail: PropTypes.string,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

function Wallet({ onChange } = {}) {
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replacing, setReplacing] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      setStatus(await b2bFetch('/api/b2b/billing'));
    } catch (e) {
      setErr(e.detail || e.message || 'Could not load your wallet.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = useCallback(async (msg) => {
    setNotice(msg);
    setReplacing(false);
    await load();
    if (onChange) await onChange();
  }, [load, onChange]);

  const removeMethod = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove the card on file? Automatic monthly charges stop until you add a new one.')) return;
    setNotice(null); setErr(null);
    try {
      await b2bFetch('/api/b2b/billing/payment-method', { method: 'DELETE' });
      setNotice('Payment method removed.');
      await load();
      if (onChange) await onChange();
    } catch (e) {
      setErr(e.detail || e.message || 'Could not remove the payment method.');
    }
  };

  if (loading) return <div style={{ ...card, textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>;

  return (
    <Panel title="Payment Method" icon="wallet2">
      <p style={{ color: c.subtle, fontSize: '0.8rem', margin: '0 0 1rem' }}>
        Add a credit card to keep on file. Your card is stored securely with our payment processor (Stripe) — we never see the full number. We charge your actual monthly Claude usage to it (plus the processor’s fee — no markup).
      </p>

      {notice && <div style={{ background: 'rgba(31,138,84,0.08)', border: `1px solid rgba(31,138,84,0.3)`, color: c.green, borderRadius: 6, padding: '0.6rem 0.85rem', fontSize: '0.82rem', marginBottom: '0.85rem' }}>{notice}</div>}
      {err && <div style={{ color: c.red, fontSize: '0.82rem', marginBottom: '0.85rem' }}>{err}</div>}

      {!status?.configured ? (
        <div style={{ color: c.subtle, fontSize: '0.85rem' }}>Online payments aren’t available yet. Please check back once your firm is fully set up.</div>
      ) : status.has_payment_method && !replacing ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: c.green, fontWeight: 600, fontSize: '0.9rem' }}>
            <i className="bi bi-check-circle-fill" />
            {status.card?.last4 ? (
              <span>
                {formatCardBrand(status.card.brand)} <span style={{ color: c.subtle, fontWeight: 700, letterSpacing: 1 }}>•••• {status.card.last4}</span>
                {status.card.exp_month && status.card.exp_year && (
                  <span style={{ color: c.subtle, fontWeight: 500, marginLeft: 8 }}>
                    exp {String(status.card.exp_month).padStart(2, '0')}/{String(status.card.exp_year).slice(-2)}
                  </span>
                )}
                {status.card.name && (
                  <span style={{ color: c.subtle, fontWeight: 500, marginLeft: 8 }}>· {status.card.name}</span>
                )}
              </span>
            ) : 'Payment method on file'}
          </div>
          <p style={{ color: c.subtle, fontSize: '0.8rem', margin: '0.5rem 0 1rem' }}>
            Automatic monthly charges are {status.payment_authorized ? 'authorized' : 'not yet authorized'}.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button onClick={() => setReplacing(true)} style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.navy, borderRadius: 6, padding: '0.5rem 0.9rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
              Replace payment method
            </button>
            <button onClick={removeMethod} style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.red, borderRadius: 6, padding: '0.5rem 0.9rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
              <i className="bi bi-arrow-counterclockwise me-1" />Remove
            </button>
          </div>
        </div>
      ) : (
        <CardForm
          publishableKey={status.publishable_key}
          billingEmail={status.billing_email}
          onSaved={onSaved}
          onCancel={replacing ? () => setReplacing(false) : null}
        />
      )}
    </Panel>
  );
}
Wallet.propTypes = { onChange: PropTypes.func };

const APP_STATUS_LABEL = {
  DRAFT: 'Not started', SUBMITTED: 'Submitted — under review', APPROVED: 'Approved',
  ACTIVE: 'Active', SUSPENDED: 'Suspended', REJECTED: 'Not approved',
};

function Onboarding({ explanationFirst = false, hideExplanation = false, onSubmitted, onChange } = {}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  // Dollar strings for the two Claude Enterprise group limits (stored as cents).
  const [userLimit, setUserLimit] = useState('');
  const [groupLimit, setGroupLimit] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const d = await b2bFetch('/api/b2b/firm');
      setData(d);
      setDomains((d.firm.email_domains || []).join(', '));
      setBillingEmail(d.firm.billing_contact_email || '');
      setUserLimit(d.firm.default_user_limit_cents != null ? String(d.firm.default_user_limit_cents / 100) : '');
      setGroupLimit(d.firm.group_limit_cents != null ? String(d.firm.group_limit_cents / 100) : '');
    } catch (e) {
      setErr(e.detail || e.message || 'Could not load onboarding.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = async (e) => {
    e.preventDefault();
    setNotice(null); setErr(null);
    // Both are required to submit — the firm can't be provisioned without a
    // domain to whitelist or a billing contact to reach.
    const domainList = domains.split(',').map(s => s.trim().replace(/^@/, '')).filter(Boolean);
    const email = billingEmail.trim();
    if (domainList.length === 0) { setErr('Enter at least one email domain to whitelist.'); return; }
    if (!email) { setErr('Enter a billing contact email.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setErr('Enter a valid billing contact email.'); return; }
    // Group user default limit is required; the firm-wide group limit is optional.
    const userDollars = parseFloat(userLimit);
    if (!(userDollars > 0)) { setErr('Enter a per-user monthly limit greater than $0.'); return; }
    let groupCents = null;
    if (groupLimit.trim() !== '') {
      const g = parseFloat(groupLimit);
      if (!(g > 0)) { setErr('Enter a group limit greater than $0, or leave it blank.'); return; }
      groupCents = Math.round(g * 100);
    }
    setSubmitting(true);
    try {
      const body = {
        email_domains: domainList,
        billing_contact_email: email,
        default_user_limit_cents: Math.round(userDollars * 100),
        group_limit_cents: groupCents,
      };
      const d = await b2bFetch('/api/b2b/firm/apply', { method: 'POST', body: JSON.stringify(body) });
      setData(d);
      setNotice('Submitted. Prosperity Capital Advisors will review your application and set up domain whitelisting.');
      if (onSubmitted) await onSubmitted();
    } catch (e2) {
      setErr(e2.detail || e2.message || 'Could not submit.');
    } finally {
      setSubmitting(false);
    }
  };

  const withdraw = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Undo your submission? Your details are kept so you can edit and resubmit.')) return;
    setNotice(null); setErr(null); setSubmitting(true);
    try {
      const d = await b2bFetch('/api/b2b/firm/withdraw', { method: 'POST' });
      setData(d);
      setNotice('Submission undone. Edit your details below and resubmit when ready.');
      if (onChange) await onChange();
    } catch (e2) {
      setErr(e2.detail || e2.message || 'Could not undo the submission.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ ...card, textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>;
  if (err && !data) return <div style={{ ...card, color: c.red, fontSize: '0.85rem' }}>{err}</div>;

  const firm = data.firm;
  const inputS = { border: `1px solid ${c.line}`, borderRadius: 6, padding: '0.55rem 0.7rem', fontSize: '0.85rem', color: c.ink, width: '100%', maxWidth: 420 };

  const explanationPanel = (
    <Panel key="explain" title="What you're signing up for" icon="shield-check">
      {explanationFirst && (
        <p style={{ color: c.subtle, fontSize: '0.85rem', lineHeight: 1.55, margin: '0 0 0.9rem' }}>
          Your firm gets its own Claude workspace under Prosperity Capital Advisors’ Enterprise account.
          Before your team can sign in, we whitelist your email domain(s) so only your people get access.
          Here’s how domain whitelisting works:
        </p>
      )}
      <ol style={{ color: c.ink, fontSize: '0.85rem', lineHeight: 1.6, margin: 0, paddingLeft: '1.2rem' }}>
        {data.domain_instructions.steps.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
      </ol>
    </Panel>
  );

  const formPanel = (
    <Panel key="form" title="Set up your firm" icon="building-gear">
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <div style={{ color: c.subtle, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Application</div>
          <div style={{ fontWeight: 700, color: c.navy }}>{APP_STATUS_LABEL[firm.application_status] || firm.application_status}</div>
        </div>
        <div>
          <div style={{ color: c.subtle, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Domain</div>
          <div style={{ fontWeight: 700, color: firm.domain_status === 'VERIFIED' ? c.green : c.amber }}>
            {firm.domain_status === 'VERIFIED' ? 'Verified' : 'Pending'}
          </div>
        </div>
      </div>

      <form onSubmit={apply} style={{ display: 'grid', gap: '0.85rem' }}>
        <label style={{ display: 'block' }}>
          <span style={{ display: 'block', color: c.subtle, fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Email domain(s) to whitelist<span style={{ color: c.red }}> *</span></span>
          <input value={domains} onChange={e => setDomains(e.target.value)} placeholder="yourfirm.com" disabled={submitting} required style={inputS} />
        </label>
        <label style={{ display: 'block' }}>
          <span style={{ display: 'block', color: c.subtle, fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Billing contact email<span style={{ color: c.red }}> *</span></span>
          <input type="email" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} placeholder="billing@yourfirm.com" disabled={submitting} required style={inputS} />
        </label>
        <label style={{ display: 'block' }}>
          <span style={{ display: 'block', color: c.subtle, fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Default monthly limit per user (USD)<span style={{ color: c.red }}> *</span></span>
          <div style={{ position: 'relative', maxWidth: 420 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: c.subtle, fontSize: '0.85rem' }}>$</span>
            <input type="number" min="0" step="1" value={userLimit} onChange={e => setUserLimit(e.target.value)} placeholder="30" disabled={submitting} required style={{ ...inputS, paddingLeft: '1.4rem' }} />
          </div>
          <span style={{ display: 'block', color: c.subtle, fontSize: '0.72rem', marginTop: 4 }}>Each new team member starts with this monthly Claude spend limit. You can raise or lower it per person later.</span>
        </label>
        <label style={{ display: 'block' }}>
          <span style={{ display: 'block', color: c.subtle, fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Firm-wide group limit (USD, optional)</span>
          <div style={{ position: 'relative', maxWidth: 420 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: c.subtle, fontSize: '0.85rem' }}>$</span>
            <input type="number" min="0" step="1" value={groupLimit} onChange={e => setGroupLimit(e.target.value)} placeholder="No cap" disabled={submitting} style={{ ...inputS, paddingLeft: '1.4rem' }} />
          </div>
          <span style={{ display: 'block', color: c.subtle, fontSize: '0.72rem', marginTop: 4 }}>A total monthly spend ceiling across your whole firm. Leave blank if you don’t want a firm-wide cap. Prosperity Capital Advisors uses this to configure your Claude Enterprise group.</span>
        </label>
        {notice && <div style={{ background: 'rgba(0,105,166,0.08)', border: `1px solid rgba(0,105,166,0.3)`, color: c.teal, borderRadius: 6, padding: '0.6rem 0.85rem', fontSize: '0.82rem', maxWidth: 420 }}>{notice}</div>}
        {err && <div style={{ color: c.red, fontSize: '0.82rem' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button type="submit" disabled={submitting} style={{ background: c.navy, color: 'white', border: 0, borderRadius: 6, padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.82rem', cursor: submitting ? 'wait' : 'pointer' }}>
            {submitting ? 'Submitting…' : (firm.application_status === 'DRAFT' ? 'Continue' : 'Update')}
          </button>
          {firm.application_status === 'SUBMITTED' && (
            <button type="button" onClick={withdraw} disabled={submitting} style={{ background: 'transparent', border: `1px solid ${c.line}`, color: c.red, borderRadius: 6, padding: '0.6rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: submitting ? 'wait' : 'pointer' }}>
              <i className="bi bi-arrow-counterclockwise me-1" />Undo submission
            </button>
          )}
        </div>
      </form>
    </Panel>
  );

  return (
    <div style={{ display: 'grid', gap: '1.25rem' }}>
      {hideExplanation
        ? [formPanel]
        : (explanationFirst ? [explanationPanel, formPanel] : [formPanel, explanationPanel])}
    </div>
  );
}
Onboarding.propTypes = {
  explanationFirst: PropTypes.bool,
  hideExplanation: PropTypes.bool,
  onSubmitted: PropTypes.func,
  onChange: PropTypes.func,
};
